import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMonthlyReport } from '../hook/useMonthlyReport.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';
import monthlyReportService from '../service/monthlyReportService.mjs';
import { AuthContext } from '../../authProvider.mjs';
import ApprovalTrail from '../../components/ApprovalTrail.mjs';
import RejectReportModal from '../../components/RejectReportModal.mjs';
import VerificationCertificate from '../components/VerificationCertificate.mjs';
import MassReviewOverlay from '../components/MassReviewOverlay.mjs';
import { generateVerificationPDF, generateMonthlyReportPDF, downloadPDF } from '../../utils/generateVerificationPDF.mjs';
import { getReportStatus, getReportClaimStatus, getReportStatusLabel }
  from '../../utils/statusConfig.mjs';
// Add this import for Excel export functionality
import ExcelJS from 'exceljs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import { useSignature } from '../../signatures/SignatureProvider.mjs';
import { finalApproverNamedOtherThan } from '../util/finalTakeover.mjs';
import { namedCoverActive, myRegionalCoverEnded, coverEndText } from '../../utils/namedCover.mjs';
import { formatInstant } from '../../utils/dateOnly.mjs';
import {
  withOpenQuestionsConfirm, confirmOpenQuestionsFirst, isCancelled,
} from '../../clarifications/util/confirmOpenQuestions.mjs';
import ClarificationPanel from '../../clarifications/components/ClarificationPanel.mjs';
import QuestionBadge from '../../clarifications/components/QuestionBadge.mjs';
import ClarificationDrawer from '../../clarifications/components/ClarificationDrawer.mjs';
import { useOpenQuestions } from '../../clarifications/hook/useOpenQuestions.mjs';
import ReportVersionViewer from '../../components/ReportVersionViewer.mjs';
import PageShell from '../../components/PageShell.mjs';

/** The page's title - in the photo band on screen, and in the printed header. */
const REPORT_TITLE = 'Per Diem & Expense Reimbursement Report';

/** How a reviewer's role reads on a flag chip. */
const REVIEW_ROLE_LABEL = {
  auditor: 'Auditor',
  regional_director: 'Regional Director',
  global_director: 'Global Director',
  finance_director: 'Finance Director',
};

/**
 * Turn a claim's review state into something visible on the row.
 *
 * Two things were previously indistinguishable: a claim SENT BACK with a reason
 * and a claim merely flagged for follow-up both rendered as the same 1-character
 * amber glyph, with the reason reachable only by hovering. A send-back is a
 * decision someone has to act on, so it is red and states itself; a flag is
 * amber. Either way the reason is on the page, not in a tooltip.
 */
const reviewNotesFor = (claim) => {
  const notes = Object.entries(claim.reviewFlags || {}).map(([role, flag]) => ({
    key: role,
    who: flag.byName || REVIEW_ROLE_LABEL[role] || role.replace(/_/g, ' '),
    role: REVIEW_ROLE_LABEL[role] || role.replace(/_/g, ' '),
    reason: flag.reason,
    at: flag.at || null,
  }));

  // A returned claim carries its reason on the claim row itself rather than in
  // reviewFlags, so it would otherwise show no explanation at all.
  const returned = claim.status === 'returned' || claim.status === 'rejected';
  if (returned && !notes.length && (claim.auditorComments || claim.comments)) {
    notes.push({
      key: 'returned',
      who: 'Auditor',
      role: 'Auditor',
      reason: claim.auditorComments || claim.comments,
      at: null,
    });
  }

  return { notes, returned };
};

const MonthlyReportPage = () => {
  const [showVersions, setShowVersions] = useState(false);
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm } = useDialog();
  // Submitting and the two director approvals are signed (2026-10-21).
  const { requestSignature } = useSignature();
  const { reportId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Where this page was opened from, so Back returns to the exact auditor tab
  // (Monthly Summary Reports vs Claims Pre-Approval) rather than always the first one.
  const originPath = routerLocation.state?.from || null;
  const { user, isRegionalDirector, isGlobalDirector, isFinanceDirector, isAuditorDelegate } = useContext(AuthContext);
  const {
    report,
    ptaes,
    loading,
    error,
    saving,
    approvalChain,
    integrityStatus,
    updateClaim,
    saveReport,
    persistReportClaims,
    submitReport: _submitReport,
    approveReport,
    rejectReport,
    auditClaimInReport,
    markClaimReviewed,
    flagClaimInReport,
    loadApprovalChain,
    verifyIntegrity,
    deleteReport,
  } = useMonthlyReport(reportId);

  const [auditorComments, setAuditorComments] = useState('');
  const [adminComments, setAdminComments] = useState('');
  const [directorComments, setDirectorComments] = useState('');
  const [sendingToAuditor, setSendingToAuditor] = useState(false);
  const [pullingClaims, setPullingClaims] = useState(false);
  const [showPullModal, setShowPullModal] = useState(false);
  const [availableClaims, setAvailableClaims] = useState([]);
  const [selectedPullIds, setSelectedPullIds] = useState(new Set());
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [expandedClaims, setExpandedClaims] = useState(new Set());
  const [showSplitModal, setShowSplitModal] = useState(null);
  const [splitData, setSplitData] = useState({
    splits: [{
      amount: '',
      ptae: '',
      ptaeSearch: '',
      showPtaeDropdown: false
    }]
  });
  const [notification, setNotification] = useState(null);
  const [showRejectReportModal, setShowRejectReportModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showApprovalTrail, setShowApprovalTrail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVerificationCertificate, setShowVerificationCertificate] = useState(false);
  const [verificationCertificate, setVerificationCertificate] = useState(null);
  const [loadingCertificate, setLoadingCertificate] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingFullPDF, setDownloadingFullPDF] = useState(false);
  const [downloadingSupportingDocs, setDownloadingSupportingDocs] = useState(false);
  const [downloadingArchive, setDownloadingArchive] = useState(false);
  // The approval email links here with ?action=download-archive|download-pdf.
  // Acted on once per visit - the handlers below are rebuilt every render.
  const emailActionHandled = useRef(false);
  // Inline individual audit state
  const [expandedAuditClaimId, setExpandedAuditClaimId] = useState(null);
  const [inlineAuditComments, setInlineAuditComments] = useState({});
  const [auditingClaimId, setAuditingClaimId] = useState(null);
  // Guided claim-by-claim review (split-screen walkthrough)
  const [showMassReview, setShowMassReview] = useState(false);
  // The claim whose clarification questions are open in the side drawer.
  const [questionClaim, setQuestionClaim] = useState(null);
  // Unresolved questions on this report and its claims, for the row badges.
  const { forClaim: questionsForClaim } = useOpenQuestions({ reportId });
  // Approval-route chooser, shown when the admin submits to the auditor.
  const [showSubmitPathModal, setShowSubmitPathModal] = useState(false);

  // Determine user role for UI display
  const isAdmin = user?.role === 'admin';
  const isAuditor = user?.role === 'auditor';
  const isDirector = isRegionalDirector || isGlobalDirector || isFinanceDirector;
  // Accurate label for the signed-in director (finance director is its own role,
  // even though it carries global-director authority).
  const directorRoleLabel = isRegionalDirector
    ? 'Regional Director'
    : isFinanceDirector
      ? 'Finance Director'
      : isGlobalDirector
        ? 'Global Director'
        // A stand-in named to cover the regional stage, who may hold no
        // director role at all. Labelling them "Global Director" would put the
        // wrong title on their comments in the approval record.
        : 'Approver';

  // Regional directors can only approve auditor_approved reports
  // Global/finance directors can only approve regional_director_approved reports
  const canApproveAsDirector = (() => {
    if (!report?.status) return false;
    // The server's verdict for this reader, while the report is still at the
    // status it was given for (2026-10-22). It is the only way the page can
    // recognise someone covering a director by delegation, who may hold any
    // role. A final approver who may take over from the one named is offered
    // approval too, behind its confirmation. The rules below are the fallback
    // for an older server, or once the report has moved on since loading.
    if (report.viewerCapacityAt && report.viewerCapacityAt === report.status) {
      return ['regional_director', 'global_director', 'finance_director'].includes(report.viewerCapacity)
        || report.viewerCanTakeOverFinal === true;
    }
    // The regional stand-in, while their cover lasts. Once it has ended the
    // naming confers nothing and the stage is back with the region's own
    // director - the server's rule (reviewCapacity), mirrored so the buttons
    // never offer what the server will refuse (2026-10-21).
    const namedRegional = report.regionalApproverId && namedCoverActive(report)
      ? String(report.regionalApproverId) : null;
    // Not gated on isDirector: an admin or ordinary user named as the regional
    // stand-in approves that stage too, which is the whole point of naming them.
    // The per-role branches below still decide who may do what.
    if (!isDirector && namedRegional !== String(user?.id)) {
      return false;
    }
    if (isRegionalDirector) {
      if (report.status !== 'auditor_approved') return false;
      // A named approver decides this stage, whoever they are. The regional
      // director still SEES a report covered by someone else - they just cannot
      // act on it - so this gates the buttons, not the page.
      if (namedRegional) {
        return namedRegional === String(user?.id);
      }
      // Excluded from the skip route - approveReport would refuse them anyway.
      return report.approvalPath !== 'skip_regional';
    }
    if (isGlobalDirector || isFinanceDirector) {
      // Named as the regional stand-in: they approve that stage, not the final one.
      if (report.status === 'auditor_approved' && namedRegional === String(user?.id)) {
        return true;
      }
      // On the 'skip_regional' route the regional director is not in the chain,
      // so final approval happens straight from auditor_approved. Without this
      // the report would sit approved-by-auditor with nobody able to act.
      if (report.approvalPath === 'skip_regional') {
        return report.status === 'auditor_approved';
      }
      return report.status === 'regional_director_approved';
    }
    // Not a director at all, but possibly named as the regional stand-in.
    return report.status === 'auditor_approved' && namedRegional === String(user?.id);
  })();

  // This reader was the regional stand-in and their cover has ended: say so,
  // instead of offering buttons the server would refuse.
  const coverEndedAt = myRegionalCoverEnded(report, user);

  // Any reviewing role can WALK a report claim-by-claim, at any stage - reading
  // the claims beside their receipts is useful whether or not it is your turn to
  // sign off. Authority to actually mark a claim is a separate question, enforced
  // by the backend per role and per report status and surfaced as `canMark` on the
  // review bundle; the walkthrough disables its own mark button accordingly.
  // Gating VISIBILITY on report status (as this once did) just made the button
  // vanish with no explanation on every report that wasn't at your exact stage.
  const isReviewerRole = isAuditor || isDirector;
  const canMassReview = isReviewerRole && report?.claims?.length > 0;

  // Who is offered "Ask a question" here: the reviewers, someone covering for
  // the auditor, and a named approver. Admins answer questions rather than ask
  // them. Only what the button shows - the server decides who may ask whom.
  const canAskQuestions = isReviewerRole || !!isAuditorDelegate || canApproveAsDirector;

  // The admin named someone else for final approval. This global or finance
  // director may still take it over - told first, asked to confirm, and the
  // named person is emailed (2026-10-21).
  const finalNamedOther = canApproveAsDirector ? finalApproverNamedOtherThan(report, user) : null;

  // Whether it is this reviewer's turn - drives the progress bar and the label,
  // not whether the walkthrough can be opened.
  const isMyReviewStage = (isAuditor && report?.status === 'submitted') || canApproveAsDirector;

  // Which per-claim field records this role's sign-off. The auditor has a real
  // individual-audit flag; the directors each have their own review marker.
  const claimReviewedByCurrentRole = (claim) => {
    if (!claim) return false;
    if (isAuditor) return !!claim.isAuditorApproved;
    if (isRegionalDirector) return !!claim.regionalDirectorReviewedAt;
    if (isGlobalDirector || isFinanceDirector) return !!claim.globalDirectorReviewedAt;
    return false;
  };

  const reviewProgressLabel = isAuditor ? 'Audited' : 'Reviewed';

  // Notification helper
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    if (report) {
      setAuditorComments(report.auditorComments || '');
      setAdminComments(report.adminComments || '');
      // Set director comments based on the user's role
      if (isRegionalDirector) {
        setDirectorComments(report.regionalDirectorComments || '');
      } else if (isGlobalDirector || isFinanceDirector) {
        setDirectorComments(report.globalDirectorComments || '');
      }
    }
  }, [report, isRegionalDirector, isGlobalDirector, isFinanceDirector]);

  // Excel Export Function using ExcelJS (secure alternative to xlsx)
  const exportToExcel = async () => {
    if (!report || !report.claims) {
      showNotification('No data available for export', 'error');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Stanford Expense System';
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet('Monthly Report');

      // Add header information
      worksheet.addRow(['Per Diem & Expense Reimbursement Report']);
      worksheet.addRow([`${new Date(0, report.month - 1).toLocaleString('default', { month: 'long' })} ${report.year} - ${report.region}`]);
      worksheet.addRow([]); // Empty row

      // Add table headers
      const headerRow = worksheet.addRow([
        'Name',
        'Type',
        'Per Diem',
        'Reimbursements',
        'Total',
        'PTA-E',
        'Allocated Amount',
        'Comments',
        'Status'
      ]);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };

      // Add claim data
      report.claims.forEach(claim => {
        const hasSplits = Array.isArray(claim.splits) && claim.splits.length > 0;

        if (hasSplits) {
          worksheet.addRow([
            claim.userName,
            claim.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem',
            claim.perDiem || 0,
            claim.reimbursements || 0,
            claim.total || 0,
            'Split',
            '-',
            claim.comments || '',
            claim.status || 'pending'
          ]);

          claim.splits.forEach((split, index) => {
            worksheet.addRow([
              `└- Split ${index + 1}`,
              '-',
              '-',
              '-',
              split.amount || 0,
              getPTAEDisplay(split.ptae),
              split.amount || 0,
              split.comments || '',
              split.status || 'pending'
            ]);
          });
        } else {
          worksheet.addRow([
            claim.userName,
            claim.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem',
            claim.perDiem || 0,
            claim.reimbursements || 0,
            claim.total || 0,
            getPTAEDisplay(claim.ptae),
            claim.allocatedAmount || 0,
            claim.comments || '',
            claim.status || 'pending'
          ]);
        }
      });

      // Add total row
      worksheet.addRow([]);
      const totalRow = worksheet.addRow(['Total Amount:', '', '', '', report.totalAmount || 0, '', '', '', '']);
      totalRow.font = { bold: true };

      // Add approval chain at the bottom - who and when, as text. Never the
      // signatures themselves (the user, 2026-10-21): those are on the report
      // PDF, the document that was signed.
      worksheet.addRow([]);
      worksheet.addRow([]);

      // Approval Chain Header
      const approvalHeaderRow = worksheet.addRow(['APPROVAL CHAIN']);
      approvalHeaderRow.font = { bold: true, size: 12 };
      approvalHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF8C1515' }
      };
      approvalHeaderRow.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

      worksheet.addRow([]);

      // Prepared By (backend returns 'preparer' association)
      const preparer = report.preparer || report.preparedBy;
      worksheet.addRow(['Prepared By:', preparer ? `${preparer.firstName} ${preparer.lastName}` : 'Unknown']);
      worksheet.addRow(['Prepared On:', new Date(report.createdAt).toLocaleString('en-US')]);

      worksheet.addRow([]);

      // Auditor Approval (backend returns 'auditorApprover' association)
      const auditorApprover = report.auditorApprover || report.auditorApprovedBy;
      if (auditorApprover || report.status === 'auditor_approved' || report.status === 'regional_director_approved' || report.status === 'global_director_approved') {
        worksheet.addRow(['Auditor Approved By:', auditorApprover ? `${auditorApprover.firstName} ${auditorApprover.lastName}` : 'Auditor']);
        worksheet.addRow(['Auditor Approved On:', report.auditorApprovedAt ? new Date(report.auditorApprovedAt).toLocaleString('en-US') : '-']);
        worksheet.addRow([]);
      }

      // Regional Director Approval (backend returns 'regionalDirectorApprover' association)
      const regionalDirectorApprover = report.regionalDirectorApprover || report.regionalDirectorApprovedBy;
      if (regionalDirectorApprover || report.status === 'regional_director_approved' || report.status === 'global_director_approved') {
        worksheet.addRow(['Regional Director Approved By:', regionalDirectorApprover ? `${regionalDirectorApprover.firstName} ${regionalDirectorApprover.lastName}` : 'Regional Director']);
        worksheet.addRow(['Regional Director Approved On:', report.regionalDirectorApprovedAt ? new Date(report.regionalDirectorApprovedAt).toLocaleString('en-US') : '-']);
        worksheet.addRow([]);
      }

      // Final Approval (backend returns 'globalDirectorApprover' association)
      const globalDirectorApprover = report.globalDirectorApprover || report.approvedBy;
      if (report.status === 'global_director_approved') {
        worksheet.addRow(['Final Approved By:', globalDirectorApprover ? `${globalDirectorApprover.firstName} ${globalDirectorApprover.lastName}` : 'Global/Finance Director']);
        worksheet.addRow(['Final Approved On:', report.globalDirectorApprovedAt || report.approvedAt ? new Date(report.globalDirectorApprovedAt || report.approvedAt).toLocaleString('en-US') : '-']);
      }

      // Set column widths
      worksheet.columns = [
        { width: 20 }, // Name
        { width: 15 }, // Type
        { width: 12 }, // Per Diem
        { width: 15 }, // Reimbursements
        { width: 12 }, // Total
        { width: 15 }, // PTA-E
        { width: 15 }, // Allocated Amount
        { width: 30 }, // Comments
        { width: 12 }  // Status
      ];

      // Generate filename
      const filename = `Monthly_Report_${new Date(0, report.month - 1).toLocaleString('default', { month: 'long' })}_${report.year}_${report.region}.xlsx`;

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      showNotification('Report exported to Excel successfully!', 'success');
    } catch (error) {
      showNotification('Failed to export report to Excel', 'error');
    }
  };

  const handleViewClaim = (claimId, claimType) => {
    if (!claimId) return showNotification('No claim ID provided', 'error');

    // Convert claimType to lowercase for consistent comparison
    const normalizedClaimType = claimType?.toLowerCase();


    const basePath =
      normalizedClaimType === 'perdiemclaim'
        ? '/perdiem/claims'  // Updated route for perdiem
        : '/expense/claims';

    try {
      navigate(`/admin${basePath}/${claimId}`);
    } catch (error) {
      secureLogger.error('Navigation error', { status: error?.status });
      showNotification('Failed to navigate to claim details', 'error');
    }
};



  // No PTAE-change handler here on purpose. Funding is assigned on the CLAIM,
  // before the report is generated; the report only DISPLAYS the code it
  // inherited. The selector this used to drive has been removed, so there is no
  // path to reassign funding from a generated report.

  const handleAllocatedAmountChange = (claimId, amount, splitIndex = null) => {
    const numericAmount = parseFloat(amount) || 0;

    if (splitIndex !== null) {
      updateClaim(claimId, (currentClaim) => {
        const updatedSplits = [...(Array.isArray(currentClaim.splits) ? currentClaim.splits : [])];
        updatedSplits[splitIndex] = { ...updatedSplits[splitIndex], amount: numericAmount };
        return { ...currentClaim, splits: updatedSplits };
      });
    } else {
      updateClaim(claimId, 'allocatedAmount', numericAmount);
    }
    setHasUnsavedChanges(true);
  };

  const handleSplitClaim = (claimId) => {
    const claim = report.claims.find(c => String(c.claimId) === String(claimId));
    if (!claim) return;

    if (Array.isArray(claim.splits) && claim.splits.length > 0) {
      // Editing an existing allocation - preload the current splits so the
      // auditor can add, remove, or re-allocate rather than being locked to the
      // set the admin created.
      setSplitData({
        splits: claim.splits.map(s => ({
          amount: (parseFloat(s.amount) || 0).toFixed(2),
          ptae: s.ptae || '',
          ptaeSearch: s.ptae || '',
          showPtaeDropdown: false
        }))
      });
    } else {
      const halfAmount = (claim.total / 2).toFixed(2);
      setSplitData({
        splits: [
          { amount: halfAmount, ptae: '', ptaeSearch: '', showPtaeDropdown: false },
          { amount: halfAmount, ptae: '', ptaeSearch: '', showPtaeDropdown: false }
        ]
      });
    }
    setShowSplitModal(claimId);
  };

  const handleAddSplit = () => {
    setSplitData(prev => ({
      ...prev,
      splits: [...prev.splits, {
        amount: '',
        ptae: '',
        ptaeSearch: '',
        showPtaeDropdown: false
      }]
    }));
  };

  const handleRemoveSplit = (index) => {
    if (splitData.splits.length <= 2) return;

    setSplitData(prev => ({
      ...prev,
      splits: prev.splits.filter((_, i) => i !== index)
    }));
  };

  const handleSplitChange = (index, field, value) => {
    setSplitData(prev => {
      const newSplits = [...prev.splits];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return { ...prev, splits: newSplits };
    });
  };

  // Handle PTAE selection specifically for split modal
  const handleSplitPTAESelection = (index, ptae) => {
    setSplitData(prev => {
      const newSplits = [...prev.splits];
      newSplits[index] = {
        ...newSplits[index],
        ptae: ptae.ptaeId,
        ptaeSearch: ptae.ptaeId,
        showPtaeDropdown: false
      };


      return { ...prev, splits: newSplits };
    });
  };

  const handleConfirmSplit = async (claimId) => {
    const claim = report.claims.find(c => String(c.claimId) === String(claimId));
    if (!claim) return;

    const totalSplit = splitData.splits.reduce((sum, split) => sum + (parseFloat(split.amount) || 0), 0);
    const originalTotal = claim.total;

    if (Math.abs(totalSplit - originalTotal) > 0.01) {
      showNotification(`Split amounts (${formatCurrency(totalSplit)}) must equal the original total (${formatCurrency(originalTotal)})`, 'error');
      return;
    }

    const splitsWithoutPtae = splitData.splits.filter(split => !split.ptae);
    if (splitsWithoutPtae.length > 0) {
      showNotification('All splits must have a PTAE assigned', 'error');
      return;
    }

    const splits = splitData.splits.map((split, index) => ({
      amount: parseFloat(split.amount),
      ptae: split.ptae,
      status: 'pending',
      comments: '',
      splitIndex: index
    }));

    // Persist the new allocation immediately (backend replaces the claim's
    // splits) so it survives a refresh instead of living only in local state.
    const nextClaims = report.claims.map(c =>
      String(c.claimId) === String(claimId) ? { ...c, splits, ptae: null } : c
    );

    try {
      await persistReportClaims(nextClaims);
      setShowSplitModal(null);
      setExpandedClaims(prev => new Set([...prev, claimId]));
      showNotification('Split allocation saved', 'success');
    } catch (err) {
      showNotification('Failed to save split: ' + err.message, 'error');
    }
  };

  const toggleClaimExpansion = (claimId) => {
    setExpandedClaims(prev => {
      const newSet = new Set(prev);
      if (newSet.has(claimId)) {
        newSet.delete(claimId);
      } else { newSet.add(claimId);
      }
      return newSet;
    });
  };

  /**
   * The route this report will take, worked out by the server and SHOWN to
   * the admin (the user, 2026-10-22). They no longer build it: covering for an
   * absent director is the director's own delegation now, and it shows here.
   * Loaded each time the submit modal opens, since cover can change any day.
   *
   * The one choice left is for a stage with nobody to sign it - no director on
   * the system for it, or theirs is suspended, and nobody covering. Then the
   * admin pulls someone in: a global or finance director for the regional
   * stage, the finance director for the final one.
   */
  const [approvalRoute, setApprovalRoute] = useState(null);
  const [routeError, setRouteError] = useState(null);
  const [regionalPick, setRegionalPick] = useState('');
  const [finalPick, setFinalPick] = useState('');

  const regionalNeedsChoice = !!approvalRoute?.regional?.needsChoice;
  const finalNeedsChoice = !!approvalRoute?.final?.needsChoice;

  const submitBlockedBecause = !approvalRoute
    ? (routeError || 'Working out the approval route…')
    : regionalNeedsChoice && approvalRoute.regional.candidates.length === 0
      ? `No regional director is available for ${report?.region || 'this region'}, and there is no global or finance director to approve that stage instead.`
      : finalNeedsChoice && approvalRoute.final.candidates.length === 0
        ? 'Nobody can give final approval: there is no active global or finance director.'
        : regionalNeedsChoice && !regionalPick
          ? 'Choose who approves the regional stage.'
          : finalNeedsChoice && !finalPick
            ? 'Choose who gives final approval.'
            : '';

  /** What the submit call sends: a person only for a stage that needs one. */
  const pathPayload = () => ({
    regionalApproverId: regionalNeedsChoice ? (regionalPick || null) : null,
    globalApproverId: finalNeedsChoice ? (finalPick || null) : null,
  });

  const openSubmitPathModal = async () => {
    setShowSubmitPathModal(true);
    setRouteError(null);
    setApprovalRoute(null);
    try {
      const route = await monthlyReportService.getApprovalRoute(reportId);
      setApprovalRoute(route);
      // One person to choose from is chosen for them; they still see it.
      const only = (stage) => (stage?.needsChoice && stage.candidates.length === 1 ? stage.candidates[0].id : '');
      setRegionalPick(only(route?.regional));
      setFinalPick(only(route?.final));
    } catch (err) {
      setRouteError(err.message);
    }
  };

  const handleSendToAuditor = async (path = {}) => {
    setSendingToAuditor(true);
    try {
      await monthlyReportService.sendToAuditor(reportId, path);
      showNotification('Report sent to auditor successfully!', 'success');
      setHasUnsavedChanges(false);
      window.location.reload();
    } catch (error) {
      showNotification('Failed to send report to auditor: ' + error.message, 'error');
    } finally {
      setSendingToAuditor(false);
    }
  };

  // Pull in claims approved after this draft was generated (or edited and
  // re-approved after a send-back), so the admin can add them before submitting.
  // Open the pull-in modal and load the approved claims available to add.
  const openPullModal = async () => {
    setShowPullModal(true);
    setSelectedPullIds(new Set());
    setLoadingAvailable(true);
    try {
      const claims = await monthlyReportService.getAvailableClaims(reportId);
      setAvailableClaims(Array.isArray(claims) ? claims : []);
    } catch (error) {
      showNotification('Failed to load available claims: ' + error.message, 'error');
      setAvailableClaims([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const togglePullId = (claimId) => {
    setSelectedPullIds(prev => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId); else next.add(claimId);
      return next;
    });
  };

  // Pull the admin-selected claims into the draft report.
  const handleConfirmPull = async () => {
    if (selectedPullIds.size === 0) {
      showNotification('Select at least one claim to add.', 'warning');
      return;
    }
    setPullingClaims(true);
    try {
      // Persist any in-progress edits FIRST. Pulling reloads the page, so
      // unsaved PTAE allocations on existing rows were being thrown away.
      if (hasUnsavedChanges) {
        await persistReportClaims(report.claims);
      }

      const result = await monthlyReportService.pullApprovedClaims(reportId, [...selectedPullIds]);
      if (result?.added > 0) {
        showNotification(`Added ${result.added} claim${result.added > 1 ? 's' : ''} to the report.`, 'success');
        setHasUnsavedChanges(false);
        setShowPullModal(false);
        window.location.reload();
      } else {
        showNotification('No claims were added.', 'info');
      }
    } catch (error) {
      showNotification('Failed to add claims: ' + error.message, 'error');
    } finally {
      setPullingClaims(false);
    }
  };

  const handleSaveProgress = async () => {
      // Check for claims without PTAE
      const claimsWithoutPtae = report.claims.filter(claim => {
        // If claim has splits, check splits instead of main claim
        if (Array.isArray(claim.splits) && claim.splits.length > 0) {
          const splitsWithoutPtae = claim.splits.filter(split => !split.ptae);
          if (splitsWithoutPtae.length > 0) {
            return true;
          }
          return false;
        } else {
          // Regular claim without splits
          if (!claim.ptae) {
            return true;
          }
          return false;
        }
      });

      if (claimsWithoutPtae.length > 0) {
        showNotification(`Cannot save: ${claimsWithoutPtae.length} claims are missing PTAE assignments. Please assign PTAEs to all claims and splits before saving.`, 'error');
        return;
      }

      try {
        // Determine the role-appropriate comments - don't change status when just saving progress
        let saveComments = adminComments;
        let saveRole = 'admin';

        if (isAuditor) {
          saveComments = auditorComments;
          saveRole = 'auditor';
        } else if (isRegionalDirector) {
          saveComments = directorComments;
          saveRole = 'regional_director';
        } else if (isGlobalDirector || isFinanceDirector) {
          saveComments = directorComments;
          saveRole = user?.role === 'finance_director' ? 'finance_director' : 'global_director';
        }

        await saveReport(null, saveComments, saveRole);
        setHasUnsavedChanges(false);
        showNotification('Progress saved successfully!', 'success');
      } catch (err) {
        secureLogger.error('Failed to save progress', { status: err.response?.status });
        showNotification('Failed to save progress: ' + err.message, 'error');
      }
    };

  // Director approval/rejection handlers
  const handleDirectorApprove = async () => {
    // The director's approval note is their own - never system-generated.
    const comments = (directorComments || '').trim();
    if (!comments) {
      showNotification(
        `Please enter your ${directorRoleLabel} comments before approving.`,
        'warning'
      );
      const el = document.getElementById('director-comments');
      if (el) { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return;
    }
    // Named to someone else: say who, and take it over only if they confirm.
    if (finalNamedOther) {
      const takeOver = await confirm(
        `The admin named ${finalNamedOther} to give final approval of this report. `
          + `You can take it over and approve it yourself. ${finalNamedOther} will be told by email.`,
        {
          title: `${finalNamedOther} was named for final approval`,
          confirmLabel: 'Take over and approve',
          cancelLabel: 'Leave it to them',
          tone: 'warning',
        }
      );
      if (!takeOver) return;
    }
    // Which stage this approval signs - the report's state decides, not the
    // role, because a named stand-in may hold no director role at all. The
    // server's awaitingStage rule: a report that skips the regional stage
    // goes from the auditor straight to final approval.
    const atFinal = report?.status === 'regional_director_approved'
      || (report?.status === 'auditor_approved' && report?.approvalPath === 'skip_regional');
    const signature = await requestSignature(atFinal
      ? {
        reportId,
        title: 'Sign to give final approval',
        reason: 'Gave final approval for this report',
        applyLabel: 'Sign and give final approval',
      }
      : {
        reportId,
        title: 'Sign to approve this report',
        reason: 'Reviewed and approved this report',
        applyLabel: 'Sign and approve',
      });
    if (!signature) return;
    setActionLoading(true);
    try {
      // Open clarification questions warn first; carrying on resends with the
      // acknowledgement - and the same signature.
      const result = await withOpenQuestionsConfirm(confirm, (acknowledgeOpenQuestions) =>
        approveReport(comments, { acknowledgeOpenQuestions, signature, takeOverFinal: !!finalNamedOther }));
      showNotification(result.message || 'Report approved successfully!', 'success');
      // Redirect based on role
      if (isRegionalDirector) {
        navigate('/regional-director-dashboard');
      } else {
        navigate('/global-director-dashboard');
      }
    } catch (err) {
      if (!isCancelled(err)) showNotification('Failed to approve report: ' + err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Whole-report send-back, shared by the auditor and both directors. Each
  // returns the entire report to the admin with a reason; only the dashboard
  // they land on afterwards differs.
  const handleReportSendBack = async (reason, comments = '') => {
    setActionLoading(true);
    try {
      const result = await rejectReport(reason, comments);
      showNotification(result.message || 'Report sent back to the admin for revision', 'success');
      setShowRejectReportModal(false);
      if (isAuditor) {
        navigate(originPath || '/auditor-dashboard');
      } else if (isRegionalDirector) {
        navigate('/regional-director-dashboard');
      } else {
        navigate('/global-director-dashboard');
      }
    } catch (err) {
      showNotification('Failed to send report back: ' + err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Verification Certificate handlers
  const handleShowVerificationCertificate = async () => {
    setLoadingCertificate(true);
    setShowVerificationCertificate(true);
    try {
      const certificate = await monthlyReportService.getVerificationCertificate(reportId);
      setVerificationCertificate(certificate);
    } catch (err) {
      showNotification('Failed to load verification certificate: ' + err.message, 'error');
      setShowVerificationCertificate(false);
    } finally {
      setLoadingCertificate(false);
    }
  };

  const handleDownloadVerificationPDF = async () => {
    if (!verificationCertificate) return;

    setDownloadingPDF(true);
    try {
      const pdfBytes = await generateVerificationPDF(verificationCertificate);
      const filename = `verification-certificate-${report.region}-${report.month}-${report.year}.pdf`;
      downloadPDF(pdfBytes, filename);
      showNotification('PDF downloaded successfully', 'success');
    } catch (err) {
      showNotification('Failed to generate PDF: ' + err.message, 'error');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Download full monthly report PDF with claims and certificate
  const handleDownloadFullReportPDF = async () => {
    setDownloadingFullPDF(true);
    try {
      // First, get the verification certificate if not already loaded
      let certificate = verificationCertificate;
      if (!certificate) {
        try {
          certificate = await monthlyReportService.getVerificationCertificate(reportId);
        } catch (certErr) {
          secureLogger.error('Could not load verification certificate', { status: certErr.response?.status });
          // Continue without certificate
        }
      }

      const monthName = new Date(0, report.month - 1).toLocaleString('default', { month: 'long' });
      const filename = `Monthly_Report_${monthName}_${report.year}_${report.region}.pdf`;

      // Prefer the server-signed PDF (PAdES): the signing key can only live on
      // the backend. Fall back to client-side generation if that call fails, so
      // a download is always possible.
      try {
        const blob = await monthlyReportService.downloadSignedReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showNotification('Signed report PDF downloaded successfully', 'success');
      } catch (signedErr) {
        secureLogger.error('Signed PDF download failed, falling back to local generation', { status: signedErr.response?.status });
        const pdfBytes = await generateMonthlyReportPDF(report, certificate);
        downloadPDF(pdfBytes, filename);
        showNotification('Report PDF downloaded (unsigned fallback)', 'warning');
      }
    } catch (err) {
      showNotification('Failed to generate PDF: ' + err.message, 'error');
    } finally {
      setDownloadingFullPDF(false);
    }
  };

  // Download every person's supporting documents - per diem AND expense - as
  // one zip, grouped by person.
  //
  // This used to open each document in its own browser tab, 400ms apart, and
  // covered per diem documents only. Expense receipts were never included, and
  // a tab per file does not survive a real report: the popup blocker keeps the
  // first and silently drops the rest.
  const handleDownloadSupportingDocs = async () => {
    setDownloadingSupportingDocs(true);
    try {
      const blob = await monthlyReportService.downloadSupportingDocumentsZip(reportId);
      const month = String(report?.month || '').padStart(2, '0');
      const name = `Report_${report?.region || 'report'}_${report?.year || ''}-${month}_supporting_documents.zip`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick: revoking synchronously can cancel the
      // download in some browsers before it has started.
      setTimeout(() => URL.revokeObjectURL(url), 0);

      showNotification(
        'Downloaded supporting documents. MANIFEST.txt inside lists every file and anything that could not be read.',
        'success',
      );
    } catch (err) {
      showNotification('Failed to download supporting documents: ' + err.message, 'error');
    } finally {
      setDownloadingSupportingDocs(false);
    }
  };

  // The final-approval archive: report, claims and every receipt in one zip.
  // Rebuilt by the server, so a big month can take a minute.
  const handleDownloadApprovalArchive = async () => {
    setDownloadingArchive(true);
    try {
      const blob = await monthlyReportService.downloadApprovalArchive(reportId);
      const monthName = new Date(0, report.month - 1).toLocaleString('en-US', { month: 'long' });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Monthly_Report_${report.region}_${monthName}_${report.year}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);

      showNotification(
        'Downloaded the approval archive. MANIFEST.txt inside says whether it still matches the approval.',
        'success',
      );
    } catch (err) {
      showNotification('Failed to download the approval archive: ' + err.message, 'error');
    } finally {
      setDownloadingArchive(false);
    }
  };

  // Links in the final-approval email: ?action=download-archive (sent when the
  // archive is too big to attach) and ?action=download-pdf. Nothing read them,
  // so both just opened the page. Run once the report has loaded, then drop the
  // parameter so a refresh does not download it again.
  //
  // The handlers are read through a ref: they are rebuilt on every render, and
  // as effect dependencies they would re-run this on every render too.
  const emailActionHandlers = useRef({});
  emailActionHandlers.current = {
    pdf: handleDownloadFullReportPDF,
    archive: handleDownloadApprovalArchive,
    notify: showNotification,
  };
  useEffect(() => {
    if (emailActionHandled.current || !report?._id) return;
    const action = new URLSearchParams(routerLocation.search).get('action');
    if (action !== 'download-archive' && action !== 'download-pdf') return;
    emailActionHandled.current = true;

    navigate(routerLocation.pathname, { replace: true, state: routerLocation.state });

    const { pdf, archive, notify } = emailActionHandlers.current;
    if (action === 'download-pdf') {
      pdf();
    } else if (report?.status === 'global_director_approved') {
      archive();
    } else {
      notify('The approval archive is only available once a report has final approval.', 'warning');
    }
  }, [report?._id, report?.status, routerLocation, navigate]);

  // Load approval chain when report loads
  useEffect(() => {
    if (report?._id) {
      loadApprovalChain(report._id);
      verifyIntegrity(report._id);
    }
  }, [report?._id, loadApprovalChain, verifyIntegrity]);

  // Inline individual claim audit from within the report view
  const handleInlineAudit = async (claimId, claimType) => {
    setAuditingClaimId(claimId);
    try {
      const comments = inlineAuditComments[claimId] || '';
      await withOpenQuestionsConfirm(confirm, (acknowledgeOpenQuestions) =>
        auditClaimInReport(claimId, claimType, comments, { acknowledgeOpenQuestions }));
      setExpandedAuditClaimId(null);
      setInlineAuditComments(prev => { const n = { ...prev }; delete n[claimId]; return n; });
      showNotification('Claim individually audited and approved', 'success');
    } catch (err) {
      if (!isCancelled(err)) showNotification('Failed to audit claim', 'error');
    } finally {
      setAuditingClaimId(null);
    }
  };

  // Reject a claim from the individual-audit box: flag the claim and roll the
  // WHOLE report back to the admin (it stays in the report, highlighted). The
  // backend hands the claim to the admin as under_review and emails them.
  const handleInlineReject = async (claimId) => {
    const comment = (inlineAuditComments[claimId] || '').trim();
    if (!comment) {
      showNotification('Please add a note explaining why this claim is being sent back', 'warning');
      return;
    }
    if (!await confirm('This will send the ENTIRE report back to the admin for revision. Continue?')) return;
    setAuditingClaimId(claimId);
    try {
      const nextClaims = report.claims.map(c =>
        String(c.claimId) === String(claimId)
          ? { ...c, status: 'returned', comments: comment, auditorComments: comment }
          : c
      );
      await persistReportClaims(nextClaims, { auditorComments: comment });
      await rejectReport(comment, comment);
      showNotification('Claim sent back - the report has been returned to the admin for revision.', 'success');
      navigate(originPath || '/auditor-dashboard');
    } catch (err) {
      showNotification('Failed to send claim back: ' + err.message, 'error');
    } finally {
      setAuditingClaimId(null);
    }
  };

  // MODIFIED: Auto-approve all line items when auditor approves report
  const handleAuditorApproval = async (approved) => {
    try {
      // Persist claim statuses first (as an explicit array - never a stale copy
      // of state), then record the auditor decision through the dedicated
      // approve/reject endpoints so it lands in the hash chain. Approving marks
      // every claim + split 'approved' so directors don't see stale 'pending'.
      if (approved) {
        // Open clarification questions warn BEFORE the rows are saved, so
        // backing out leaves the report exactly as it was.
        if (!await confirmOpenQuestionsFirst(confirm, { reportId: report._id })) return;
        const nextClaims = report.claims.map(claim => {
          if (Array.isArray(claim.splits) && claim.splits.length > 0) {
            return { ...claim, status: 'approved', splits: claim.splits.map(s => ({ ...s, status: 'approved' })) };
          }
          return { ...claim, status: 'approved' };
        });
        await persistReportClaims(nextClaims, { auditorComments });
        await approveReport(auditorComments, { acknowledgeOpenQuestions: true });
      } else {
        // Persist any per-claim status changes (e.g. claims marked "returned")
        // first so they survive, then record the report-level send-back. The
        // backend hands each returned claim to the admin as 'under_review'.
        const reason = auditorComments.trim() || 'Sent back for revision';
        await saveReport(null, reason, 'auditor');
        await rejectReport(reason, reason);
      }
      showNotification(approved ? 'Report approved! All line items have been automatically approved.' : 'Report sent back for revision.', 'success');
      navigate(originPath || '/auditor-dashboard');
    } catch (err) {
      secureLogger.error('Failed to update report', { status: err.response?.status });
      showNotification(`Failed to ${approved ? 'approve' : 'send back'} report: ${err.message}`, 'error');
    }
  };

  // Finishing the guided walkthrough hands off to the existing report-level
  // actions. The overlay is closed first on purpose: both paths can bounce back
  // with a validation notification (a director must enter their own comments) and
  // the send-back modal lives on the page beneath, so neither would be visible
  // with the full-screen walkthrough still up.
  const handleMassReviewApprove = async () => {
    setShowMassReview(false);
    if (isAuditor) {
      await handleAuditorApproval(true);
    } else {
      await handleDirectorApprove();
    }
  };

  const handleMassReviewSendBack = () => {
    setShowMassReview(false);
    setShowRejectReportModal(true);
  };

  // Neither mass approval nor mass send-back exists any more. The auditor signs
  // off each claim with the per-row Audit control, then acts on the report as a
  // whole with Approve Report / Send Back for Revision.

  // Remove a claim from a DRAFT report (including ones just pulled in). The
  // claim itself is untouched - it stays approved and becomes eligible for a
  // later report. Every other row is passed through byte-for-byte so their PTAE
  // allocations and splits survive the save.
  const handleRemoveClaimFromReport = async (claimId) => {
    const claim = report.claims.find(c => String(c.claimId) === String(claimId));
    if (!claim) return;
    if (!await confirm(
      `Remove ${claim.userName || 'this claim'} from the report? ` +
      `The claim stays approved and can be pulled into a later report.`
    )) return;

    const nextClaims = report.claims.filter(c => String(c.claimId) !== String(claimId));
    try {
      await persistReportClaims(nextClaims);
      showNotification('Claim removed from the report', 'success');
    } catch (err) {
      showNotification('Failed to remove claim: ' + err.message, 'error');
    }
  };

  // Delete a DRAFT report. It has never been submitted, so nobody has reviewed
  // or signed it; anything later is part of the audit record and the server
  // refuses it. The claims are released for the next report generated.
  const handleDeleteDraft = async () => {
    if (!await confirm(
      'Delete this draft report? Its claims stay approved and will be picked up by the next report you generate.',
      { title: 'Delete draft report', confirmLabel: 'Delete draft', danger: true }
    )) return;
    try {
      await deleteReport();
      navigate('/dashboard/monthly-reports');
    } catch (err) {
      showNotification('Failed to delete the draft: ' + err.message, 'error');
    }
  };

  // Revert a split claim back to a single line item (removes all splits, keeps
  // the first split's PTAE and the full amount). Available to admin and auditor.
  const handleUnsplit = async (claimId) => {
    const claim = report.claims.find(c => String(c.claimId) === String(claimId));
    if (!claim || !Array.isArray(claim.splits) || claim.splits.length === 0) return;
    if (!await confirm('Revert this split claim back to a single line item? The split allocations will be removed.')) return;

    const firstPtae = claim.splits[0]?.ptae || claim.ptae || null;
    const nextClaims = report.claims.map(c =>
      String(c.claimId) === String(claimId)
        ? { ...c, splits: [], ptae: firstPtae, allocatedAmount: c.total }
        : c
    );
    try {
      await persistReportClaims(nextClaims);
      showNotification('Reverted to a single line item', 'success');
    } catch (err) {
      showNotification('Failed to revert split: ' + err.message, 'error');
    }
  };


  // Which stage sent the report back - covers all three rejecting parties.
  const getRejectionStageLabel = (stage) => ({
    auditor_rejected: 'Auditor',
    regional_director_rejected: 'Regional Director',
    global_director_rejected: 'Global / Finance Director',
  }[stage] || '');


  const formatCurrency = (amount) => {
    // Coerce robustly: DECIMAL values arrive as strings and may carry separators;
    // guard against NaN so the total never renders as "NaN".
    const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? '').replace(/[^0-9.-]/g, ''));
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number.isFinite(n) ? n : 0);
  };

  // A generated report is a RECORD, not a worksheet - no field on it is
  // editable by anyone. Funding is assigned on the claim before generation
  // (approval is blocked without a PTAE), so the report inherits correct
  // allocations rather than being corrected here.
  const canEditReport = () => false;

  // NEW: Function to check if splits can be created
  // Splitting/un-splitting is an ADMIN-only action. Auditors review and comment;
  // they do not restructure a claim's PTAE allocations.
  // Splitting is done on the claim (PTA allocation), not on the report.
  const canCreateSplits = () => false;

  const canSendToAuditor = () => {
    // Only a draft can go to the auditor. A report the auditor sent back is
    // defunct: its figures have already been reviewed and rejected, so the admin
    // corrects the underlying claims and generates a fresh report rather than
    // resubmitting the same one and hiding that history.
    return isAdmin && report.status === 'draft' && !hasUnsavedChanges;
  };

  const canAuditorReview = () => {
    return isAuditor && report.status === 'submitted';
  };

  // NEW: Function to check if auditor can save progress
  const canAuditorSave = () => {
    return isAuditor && report.status === 'submitted';
  };

  // Updated PTAE display function to show the full PTAE code
  const getPTAEDisplay = (ptaeCode) => {
    if (!ptaeCode) return '';
    const ptae = ptaes.find(p => p.ptaeId === ptaeCode);
    return ptae ? ptae.ptaeId : ptaeCode;
  };

  const getTotalSplitAmount = () => {
    return splitData.splits.reduce((sum, split) => sum + (parseFloat(split.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    if (!showSplitModal) return 0;
    const claim = report.claims.find(c => String(c.claimId) === String(showSplitModal));
    if (!claim) return 0;
    return claim.total - getTotalSplitAmount();
  };

  // Display helper function for status - now handles 'returned' status properly
  // Claims are listed per person, so grouping by name makes a report readable
  // and lets a reviewer find someone without scanning. Falls back to claim id
  // for rows with no name, and localeCompare keeps accented names in order.
  const byIndividual = (a, b) => {
    const an = (a?.userName || '').trim();
    const bn = (b?.userName || '').trim();
    if (an && bn && an !== bn) return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    if (an !== bn) return an ? -1 : 1;
    return String(a?.claimId || '').localeCompare(String(b?.claimId || ''));
  };

  const sortedReportClaims = useMemo(
    () => [...(report?.claims || [])].sort(byIndividual),
    [report]
  );
  const renderClaimRow = (claim, index) => {
    const isExpanded = expandedClaims.has(claim.claimId);
    const hasSplits = Array.isArray(claim.splits) && claim.splits.length > 0;

    // Clicking any informational cell opens the claim, exactly as View does, so
    // a reviewer does not have to hunt for the button. Cells that hold their own
    // controls (PTAE picker, allocation input, comments, audit actions) keep
    // their behaviour and are deliberately excluded.
    const openClaim = () => handleViewClaim(claim.claimId, claim.claimType);
    const clickCell = 'cursor-pointer hover:text-stanford-red';
    const openProps = {
      onClick: openClaim,
      title: 'Open this claim',
    };

    const { notes: reviewNotes, returned: isReturned } = reviewNotesFor(claim);

    return (
      <React.Fragment key={claim.claimId || index}>
        {/* MAIN CLAIM ROW */}
        <tr className={`rpt-claim-row border-b border-gray-200 hover:bg-gray-50 ${
          isReturned
            ? 'rpt-row-returned bg-red-50 border-l-4 border-l-red-500'
            : reviewNotes.length > 0
              ? 'rpt-row-flagged bg-amber-50 border-l-4 border-l-amber-500'
              : hasSplits ? 'bg-gray-50' : ''
        }`}>
          {/* On phones the expand toggle moves into the Actions cell as a
              labelled full-width button (see below). */}
          <td className="rpt-col-toggle hidden md:table-cell px-3 py-3 w-8">
            {hasSplits && (
              <button
                onClick={() => toggleClaimExpansion(claim.claimId)}
                className="text-gray-500 hover:text-gray-700 font-mono"
                title={isExpanded ? 'Collapse splits' : 'Expand splits'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
          </td>

          <td data-cell="title" className={`rpt-col-name px-3 py-3 text-sm text-gray-800 font-medium ${clickCell}`} {...openProps}>
            <span className="flex flex-wrap items-center gap-1.5">
              {claim.userName}
              {/* Red for a claim sent back, amber for one flagged for follow-up.
                  Attributed per role so a director's concern sits alongside the
                  auditor's rather than replacing it. */}
              {reviewNotes.length > 0 && (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${
                  isReturned
                    ? 'bg-red-100 text-red-800'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {isReturned ? '↩ Sent back' : `Flagged${reviewNotes.length > 1 ? ` ×${reviewNotes.length}` : ''}`}
                </span>
              )}
              {/* Open clarification questions on this claim; opens them. */}
              <QuestionBadge
                threads={questionsForClaim(claim.claimType, claim.claimId)}
                onClick={() => setQuestionClaim(claim)}
                className="no-print"
              />
            </span>
          </td>
          <td data-label="Type" className={`rpt-col-type px-3 py-3 ${clickCell}`} {...openProps}>
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
              claim.claimType === 'ExpenseClaim' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {claim.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem'}
            </span>
          </td>
          <td data-label="Per Diem" className={`rpt-col-perdiem px-3 py-3 text-sm text-gray-700 text-right tabular-nums ${clickCell}`} {...openProps}>{formatCurrency(claim.perDiem)}</td>
          <td data-label="Reimbursements" className={`rpt-col-reimb px-3 py-3 text-sm text-gray-700 text-right tabular-nums ${clickCell}`} {...openProps}>{formatCurrency(claim.reimbursements)}</td>
          <td data-label="Total" className={`rpt-col-total px-3 py-3 text-sm font-semibold text-gray-800 text-right tabular-nums ${clickCell}`} {...openProps}>{formatCurrency(claim.total)}</td>

          <td data-label="PTA-E" className="rpt-col-ptae px-3 py-3">
            {/* Funding is assigned on the CLAIM, before the report is
                generated - never here. The report is a record of what was
                approved, so this is display only. */}
            {!hasSplits ? (
              (
                <span className={`min-w-0 text-sm text-gray-600 break-all md:break-normal md:whitespace-nowrap font-mono ${clickCell}`} {...openProps}>
                  {claim.ptae ? getPTAEDisplay(claim.ptae) : 'Not Set'}
                </span>
              )
            ) : (
              /* Show the actual PTAEs a split claim is allocated to, in full,
                 rather than an opaque "Split" label. */
              <div className={`min-w-0 flex flex-col items-end md:items-stretch gap-0.5 ${clickCell}`} {...openProps}>
                {(claim.splits || []).map((sp, i) => (
                  <span key={i} className="text-xs text-gray-700 break-all md:break-normal md:whitespace-nowrap font-mono">
                    {sp.ptae ? getPTAEDisplay(sp.ptae) : 'Not Set'}
                  </span>
                ))}
              </div>
            )}
          </td>

          <td data-label="Allocated Amount" className="rpt-col-alloc px-3 py-3 tabular-nums">
            {!hasSplits ? (
              canEditReport() ? (
                <input
                  type="number"
                  step="0.01"
                  value={claim.allocatedAmount || ''}
                  onChange={(e) => handleAllocatedAmountChange(claim.claimId, e.target.value)}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  min="0"
                />
              ) : (
                <span className="text-sm text-gray-700">
                  {formatCurrency(claim.allocatedAmount)}
                </span>
              )
            ) : (
              <span className="text-sm text-gray-500">-</span>
            )}
          </td>

          {!isDirector && <td
            data-label="Comments"
            /* An editable box sits under its label on a phone, not beside it. */
            data-cell={canEditReport() || (isAuditor && canAuditorReview()) ? 'full' : undefined}
            className="rpt-col-comments px-3 py-3"
          >
            {/* Printed: the comment text itself, blank when there is none -
                never the box or its placeholder. */}
            {canEditReport() || (isAuditor && canAuditorReview()) ? (
              <>
                <textarea
                  value={claim.comments || ''}
                  onChange={(e) => {
                    updateClaim(claim.claimId, 'comments', e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="no-print w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-stanford-red focus:border-transparent resize-none"
                  placeholder="Claim comments..."
                  rows="1"
                />
                {claim.comments && <span className="rpt-screen-hide">{claim.comments}</span>}
              </>
            ) : (
              <div className={`text-sm text-gray-600${claim.comments ? '' : ' no-print'}`}>
                {claim.comments || '-'}
              </div>
            )}
          </td>}

          {/* Individual audit cell - auditor only */}
          {isAuditor && (
            <td
              data-label="Audit"
              /* The open audit panel takes the card's full width on a phone. */
              data-cell={!(claim.isAuditorApproved || claim.auditorApprovedAt) && expandedAuditClaimId === claim.claimId ? 'full' : undefined}
              className="rpt-col-audit px-3 py-3"
            >
              {(claim.isAuditorApproved || claim.auditorApprovedAt) ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" title={`Audited ${claim.auditorApprovedAt ? new Date(claim.auditorApprovedAt).toLocaleDateString() : ''}`}>
                  ✓ Audited
                </span>
              ) : expandedAuditClaimId === claim.claimId ? (
                <div className="min-w-[180px] p-3 md:p-2 bg-amber-50 border border-amber-200 rounded-lg relative">
                  {/* Close without acting. */}
                  <button
                    onClick={() => setExpandedAuditClaimId(null)}
                    className="absolute top-1 right-1 w-11 h-11 md:w-auto md:h-auto flex md:block items-center justify-center text-gray-400 hover:text-gray-600 text-xl md:text-sm leading-none md:leading-none"
                    aria-label="Close"
                  >
                    ×
                  </button>
                  {/* Review the claim read-only before deciding. */}
                  <button
                    onClick={() => handleViewClaim(claim.claimId, claim.claimType, true)}
                    className="w-[calc(100%-3rem)] md:w-full min-h-[44px] md:min-h-0 mb-2 mt-1 px-2 py-1 text-sm md:text-xs border border-blue-300 text-blue-700 bg-white rounded hover:bg-blue-50 transition-colors"
                    title="Open this claim read-only before deciding"
                  >
                    View claim
                  </button>
                  <textarea
                    value={inlineAuditComments[claim.claimId] || ''}
                    onChange={(e) => setInlineAuditComments(prev => ({ ...prev, [claim.claimId]: e.target.value }))}
                    placeholder="Notes (required to reject)..."
                    rows={2}
                    className="block md:inline-block w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none mb-2 focus:ring-1 focus:ring-stanford-red focus:border-transparent"
                  />
                  <div className="flex gap-2 md:gap-1">
                    <button
                      onClick={() => handleInlineReject(claim.claimId)}
                      disabled={auditingClaimId === claim.claimId}
                      className="flex-1 min-h-[44px] md:min-h-0 px-2 py-1 text-sm md:text-xs bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors disabled:opacity-50"
                      title="Send this claim back - returns the whole report to the admin"
                    >
                      ↩ Reject
                    </button>
                    <button
                      onClick={() => handleInlineAudit(claim.claimId, claim.claimType)}
                      disabled={auditingClaimId === claim.claimId}
                      className="flex-1 min-h-[44px] md:min-h-0 px-2 py-1 text-sm md:text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    >
                      {auditingClaimId === claim.claimId ? '...' : '✓ Audit'}
                    </button>
                  </div>
                </div>
              ) : canAuditorReview() ? (
                <button
                  onClick={() => {
                    setExpandedAuditClaimId(claim.claimId);
                    // Seed from the row's Comments cell: a note typed there is
                    // about this claim, and it is what gets recorded on the
                    // audit either way. Seeding makes that visible instead of
                    // presenting an empty second box.
                    setInlineAuditComments(prev => (
                      prev[claim.claimId] !== undefined
                        ? prev
                        : { ...prev, [claim.claimId]: claim.auditorComments || claim.comments || '' }
                    ));
                  }}
                  className="min-h-[44px] md:min-h-0 px-5 md:px-2 py-1 text-sm md:text-xs border border-amber-300 text-amber-700 bg-amber-50 rounded hover:bg-amber-100 transition-colors"
                >
                  Audit
                </button>
              ) : (
                /* Report already approved/closed - no auditing or send-back. */
                <span className="text-sm text-gray-400">-</span>
              )}
            </td>
          )}

          <td data-cell="action" className="rpt-col-actions px-3 py-3">
            <div className="flex gap-1">
              {/* Phone-only stand-in for the ▶ column, which is hidden there. */}
              {hasSplits && (
                <button
                  onClick={() => toggleClaimExpansion(claim.claimId)}
                  aria-expanded={isExpanded}
                  className="md:hidden px-2 py-1 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors"
                >
                  {isExpanded ? 'Hide splits' : `Show ${claim.splits.length} split${claim.splits.length === 1 ? '' : 's'}`}
                </button>
              )}
              <button
                onClick={() => handleViewClaim(claim.claimId, claim.claimType, true)}
                className="px-2 py-1 text-sm md:text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors"
                title="View claim details"
              >
                 View
              </button>

              {/* Admin/auditor can create splits on an un-split claim, and edit the
                  allocation (add/remove/re-allocate) on an already-split one. */}
              {canCreateSplits() && (
                <button
                  onClick={() => handleSplitClaim(claim.claimId)}
                  className="px-2 py-1 text-sm md:text-xs border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 transition-colors"
                  title={hasSplits ? 'Edit PTAE allocations (add or remove splits)' : 'Split into multiple PTAE allocations'}
                >
                  {hasSplits ? 'Edit Splits' : 'Split'}
                </button>
              )}

              {/* Admin can drop a claim from a draft report. */}
              {isAdmin && report.status === 'draft' && (
                <button
                  onClick={() => handleRemoveClaimFromReport(claim.claimId)}
                  className="px-2 py-1 text-sm md:text-xs border border-red-300 text-red-700 rounded hover:bg-red-50 transition-colors"
                  title="Remove this claim from the report (the claim itself is not deleted)"
                >
                  Remove
                </button>
              )}

              {/* Revert an already-split claim back to a single line item. */}
              {canCreateSplits() && hasSplits && (
                <button
                  onClick={() => handleUnsplit(claim.claimId)}
                  className="px-2 py-1 text-sm md:text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors"
                  title="Revert to a single line item (remove all splits)"
                >
                  Un-split
                </button>
              )}
            </div>
          </td>

          <td data-label="Status" className="rpt-col-status px-3 py-3">
            {/* A report LINE, so the per-claim vocabulary: "Not Yet Reviewed"
                rather than the raw token capitalised behind a glyph. */}
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getReportClaimStatus(claim.status).badge}`}>
              {getReportClaimStatus(claim.status).label}
            </span>
          </td>
        </tr>

        {/* REVIEW NOTES - the reason a claim was sent back or flagged, in full.
            Its own row rather than a tooltip: this is the thing the admin has to
            act on, and it was previously invisible without hovering a glyph. */}
        {reviewNotes.length > 0 && (
          <tr className={`report-notes-row ${isReturned
            ? 'rpt-row-returned bg-red-50 max-md:border-l-4 max-md:border-l-red-500'
            : 'rpt-row-flagged bg-amber-50 max-md:border-l-4 max-md:border-l-amber-500'}`}>
            {/* Deliberately over-wide: three of this table's columns are
                role-conditional, and an over-large colSpan is clamped to the
                real width rather than drifting as those conditions change. */}
            <td colSpan={12} data-cell="full" className="px-3 pt-0 pb-3">
              <div className="flex flex-col gap-1 pl-0 md:pl-6">
                {reviewNotes.map(note => (
                  <div
                    key={note.key}
                    className={`text-xs break-words border-l-2 pl-2 ${
                      isReturned
                        ? 'border-l-red-400 text-red-900'
                        : 'border-l-amber-400 text-amber-900'
                    }`}
                  >
                    <span className="font-semibold">{note.role}</span>
                    {note.who && note.who !== note.role && (
                      <span className="rpt-note-meta opacity-80"> · {note.who}</span>
                    )}
                    {note.at && (
                      <span className="rpt-note-meta opacity-60">
                        {' '}· {new Date(note.at).toLocaleDateString('en-US')}
                      </span>
                    )}
                    <span className="opacity-90">: {note.reason}</span>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        )}

        {/* SPLIT ROWS - rendered for every split claim so the printed report
            lists each allocation under its claim; on screen a collapsed
            claim's rows are hidden (rpt-screen-hide), exactly as before. */}
        {hasSplits && (claim.splits || []).map((split, splitIndex) => (
          <tr key={`${claim.claimId}-split-${splitIndex}`} className={`report-split-row bg-gray-100 border-b border-gray-200${isExpanded ? '' : ' rpt-screen-hide'}`}>
            {/* One cell per header column. An extra leading auditor-only cell
                used to sit here, pushing every split cell one column right. */}
            <td className="rpt-col-toggle"></td>
            <td data-cell="title" className="rpt-col-name px-3 py-2 text-sm text-gray-500 pl-8">└- Split {splitIndex + 1}</td>

            <td className="rpt-col-type hidden md:table-cell px-3 py-2 text-sm text-gray-400">-</td>
            <td className="rpt-col-perdiem hidden md:table-cell px-3 py-2 text-sm text-gray-400">-</td>
            <td className="rpt-col-reimb hidden md:table-cell px-3 py-2 text-sm text-gray-400">-</td>
            <td data-label="Amount" className="rpt-col-total px-3 py-2 text-sm text-gray-700 text-right tabular-nums">{formatCurrency(split.amount)}</td>

            <td data-label="PTA-E" className="rpt-col-ptae px-3 py-2">
              <span className="min-w-0 text-sm text-gray-600 break-all md:break-normal">
                {split.ptae ? getPTAEDisplay(split.ptae) : 'Not Set'}
              </span>
            </td>

            <td data-label="Allocated Amount" className="rpt-col-alloc px-3 py-2 tabular-nums">
              {canEditReport() ? (
                <input
                  type="number"
                  step="0.01"
                  value={split.amount || ''}
                  onChange={(e) => handleAllocatedAmountChange(claim.claimId, e.target.value, splitIndex)}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  min="0"
                />
              ) : (
                <span className="text-sm text-gray-700">
                  {formatCurrency(split.amount)}
                </span>
              )}
            </td>

            {!isDirector && <td
              data-label="Comments"
              data-cell={canEditReport() || (isAuditor && canAuditorReview()) ? 'full' : undefined}
              className="rpt-col-comments px-3 py-2"
            >
              {canEditReport() || (isAuditor && canAuditorReview()) ? (
                <>
                <textarea
                  value={split.comments || ''}
                  onChange={(e) => {
                    updateClaim(claim.claimId, (currentClaim) => {
                      const currentSplits = Array.isArray(currentClaim.splits) ? currentClaim.splits : [];
                      const updatedSplits = currentSplits.map((existingSplit, index) => {
                        if (index === splitIndex) {
                          return { ...existingSplit, comments: e.target.value };
                        }
                        return existingSplit;
                      });
                      return { ...currentClaim, splits: updatedSplits };
                    });
                    setHasUnsavedChanges(true);
                  }}
                  className="no-print w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-stanford-red focus:border-transparent resize-none"
                  placeholder="Split comments..."
                  rows="1"
                />
                {split.comments && <span className="rpt-screen-hide">{split.comments}</span>}
                </>
              ) : (
                <div className={`text-sm text-gray-600${split.comments ? '' : ' no-print'}`}>
                  {split.comments || '-'}
                </div>
              )}
            </td>}

            {/* Empty audit column cell for splits */}
            {isAuditor && <td className="rpt-col-audit"></td>}

            {/* Splits are not approved/rejected individually - the decision is
                made on the parent claim line. */}
            <td className="rpt-col-actions px-3 py-2"></td>

            <td data-label="Status" className="rpt-col-status px-3 py-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getReportClaimStatus(split.status || 'pending').badge}`}>
                {getReportClaimStatus(split.status || 'pending').label}
              </span>
            </td>
          </tr>
        ))}
        {/* The dark theme stripes tbody rows by nth-child. Hidden split rows
            still count, so an odd number of them gets one more (never shown)
            to keep every later row on the stripe it had before. */}
        {hasSplits && !isExpanded && claim.splits.length % 2 === 1 && (
          <tr className="rpt-parity-row" aria-hidden="true" />
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <PageShell title={REPORT_TITLE} width="wide" photo={false}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading monthly report...</p>
        </div>
      </PageShell>
    );
  }

  // Determine the appropriate dashboard based on user role
  const getDashboardPath = () => {
    if (originPath) return originPath;
    if (isAuditor) return '/auditor-dashboard';
    if (isRegionalDirector) return '/regional-director-dashboard';
    if (isGlobalDirector || isFinanceDirector) return '/global-director-dashboard';
    if (isAdmin) return '/dashboard/monthly-reports';
    return '/dashboard/monthly-reports'; // Default for admin/regular users
  };

  if (error) {
    return (
      <PageShell title={REPORT_TITLE} width="wide" photo={false}>
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-md mx-auto text-center">
          <svg className="w-10 h-10 mx-auto mb-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Report</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate(getDashboardPath())} className="px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors">
            Back to Dashboard
          </button>
        </div>
      </PageShell>
    );
  }

  if (!report) {
    return (
      <PageShell title={REPORT_TITLE} width="wide" photo={false}>
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-md mx-auto text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Report Not Found</h3>
          <p className="text-gray-600 mb-4">The requested monthly report could not be found.</p>
          <button onClick={() => navigate(getDashboardPath())} className="px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors">
            Back to Dashboard
          </button>
        </div>
      </PageShell>
    );
  }

  const monthName = new Date(0, report.month - 1).toLocaleString('default', { month: 'long' });

  return (
    <>
      {/* NOTIFICATION - a sibling of the shell, so it layers against the fixed
          navbar on its own z-index. */}
      {notification && (
        <div className={`fixed top-3 left-3 right-3 sm:top-5 sm:left-auto sm:right-5 px-5 py-3 rounded-lg text-white font-medium z-[10000] flex items-center justify-between sm:min-w-[300px] shadow-lg animate-slide-in ${
          notification.type === 'success' ? 'bg-green-500' :
          notification.type === 'error' ? 'bg-red-500' :
          notification.type === 'warning' ? 'bg-amber-500' :
          'bg-blue-500'
        }`}>
          {notification.message}
          <button onClick={() => setNotification(null)} aria-label="Dismiss" className="ml-3 -mr-2 sm:mr-0 w-11 h-11 sm:w-auto sm:h-auto flex-shrink-0 text-xl font-bold hover:opacity-80">&times;</button>
        </div>
      )}

      {/* print:!static - the print rules pin .monthly-report-page to the page's
          top-left (position:absolute); the shell's column is position:relative,
          which would otherwise re-anchor it inside the body margin. */}
      <PageShell
        title={REPORT_TITLE}
        photo={false}
        subtitle={`${monthName} ${report.year} · ${report.region}`}
        width="wide"
        className="print:!static"
      >
      {/* .monthly-report-page is what the print rules keep (everything outside
          it is hidden when printing). Its own sheet styles (white fill, 20/50px
          padding) are switched off: on screen the cards sit on the page shell,
          and on paper the @page margin is the sheet's margin (index.css:
          MONTHLY REPORT - PRINTED DOCUMENT lays out the printed report). */}
      <div className="monthly-report-page !p-0 !bg-transparent">
        {/* HEADER SECTION - PDF Style. On screen the title is in the photo band;
            the band never prints, so the report's own red header prints here. */}
        <div className="rpt-head-card shadow-lg overflow-hidden mb-4 sm:mb-6 rounded-xl print:rounded-none">
          {/* Stanford Red Header - print only */}
          <div className="rpt-title-band hidden print:block bg-stanford-red text-white px-5 py-4 sm:px-6 sm:py-5">
            <div className="flex flex-col items-center text-center">
              <h1 className="text-xl sm:text-2xl font-bold tracking-wide text-white">{REPORT_TITLE}</h1>
              <h2 className="text-base sm:text-lg mt-2 text-red-100">
                {monthName} {report.year} - {report.region}
              </h2>
              <p className="text-sm text-red-200 mt-1">Stanford Seed Expense Claim System</p>
            </div>
          </div>

          {/* The white fill is on this inner box, not the rounded/shadowed one
              above: ".bg-white.rounded-xl.shadow-lg" has its own print rule
              (border + 12px padding) that would inset the printed header.
              It is deliberately not the .bg-white class: in dark mode
              "html.dark .bg-white span/p" repaints every pill and banner line
              in here light gray (violet/slate status pills and the admin's
              sent-back note went blank). Same colors: white / gray-800. */}
          <div className="bg-[#ffffff] dark:bg-gray-800">
          {/* Report Summary Bar */}
          <div className="rpt-summary-bar px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="rpt-summary grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Region</span>
                <span className="font-semibold text-gray-800">{report.region}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Period</span>
                <span className="font-semibold text-gray-800">
                  {new Date(0, report.month - 1).toLocaleString('default', { month: 'long' })} {report.year}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Total Claims</span>
                <span className="font-semibold text-gray-800">{report.claims?.length || 0}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Total Amount</span>
                <span className="font-bold text-stanford-red text-lg">
                  {formatCurrency(report.totalAmount)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Status</span>
                {(() => {
                  const meta = getReportStatus(report.status, report, {
                    viewerRole: user?.role, viewerId: user?.id,
                  });
                  return (
                    <>
                      <span
                        title={meta.description}
                        className={`no-print inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${meta.badge}`}
                      >
                        {meta.label}
                      </span>
                      {/* Printed: the report's own status. "Awaiting Your
                          Review" means nothing to whoever reads the paper. */}
                      <span className="rpt-screen-hide">{getReportStatus(report.status, report).label}</span>
                    </>
                  );
                })()}
              </div>
              <div>
                <span className="text-gray-500 block">Version</span>
                {/* Opens the archive. This read as a plain number before, which
                    named a version the reader had no way to look inside. */}
                <button
                  type="button"
                  onClick={() => setShowVersions(true)}
                  className="min-h-[44px] sm:min-h-0 font-semibold text-stanford-red hover:underline focus:outline-none focus:ring-2 focus:ring-stanford-red rounded"
                  title="View previous versions of this report"
                >
                  {report.version || 1}
                  <span className="ml-1.5 text-xs font-normal text-gray-500">view history</span>
                </button>
                {/* Printed: the number alone (the button does not print). */}
                <span className="rpt-screen-hide">{report.version || 1}</span>
              </div>
            </div>
          </div>

          {/* Sent-back banner - who rejected it, at which stage, and why. Shown
              for rejections from ANY stage (auditor, regional, global/finance)
              so the admin can act before sending it back up the chain. */}
          {report.status === 'rejected' && (
            <div className="rpt-sentback mx-4 sm:mx-6 mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-lg px-4 py-3">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                <span className="text-amber-800 font-semibold">↩ Sent Back for Revision</span>
                <span className="text-amber-700 text-sm">
                  by {report.rejector
                        ? `${report.rejector.firstName} ${report.rejector.lastName}`
                        : 'a reviewer'}
                  {getRejectionStageLabel(report.rejectionStage) && ` (${getRejectionStageLabel(report.rejectionStage)})`}
                  {report.rejectedAt && ` on ${new Date(report.rejectedAt).toLocaleDateString()}`}
                </span>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                <span className="font-medium">Reason: </span>
                {report.rejectionReason || report.auditorComments || 'No reason provided.'}
              </p>
              {/* What the admin does next - screen guidance, not part of the record. */}
              {isAdmin && (
                <p className="no-print text-sm text-amber-900 mt-2">
                  This report is closed. Correct the flagged claims, then generate a new
                  monthly report for this period - it cannot be resubmitted.
                </p>
              )}
            </div>
          )}

          {/* Action Links */}
          <div className="no-print bg-white px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4 max-sm:empty:hidden">
              {integrityStatus && (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  integrityStatus.valid
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {integrityStatus.valid ? '✓ Integrity Verified' : '✗ Integrity Issue'}
                </span>
              )}
            </div>
            <div className="w-full sm:w-auto grid grid-cols-2 sm:flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => setShowApprovalTrail(!showApprovalTrail)}
                aria-expanded={showApprovalTrail}
                className="min-h-[44px] sm:min-h-0 text-sm text-stanford-red hover:underline font-medium"
              >
                {showApprovalTrail ? 'Hide Approval History' : 'View Approval History'}
              </button>
              <button
                onClick={handleShowVerificationCertificate}
                className="min-h-[44px] sm:min-h-0 text-sm text-green-700 hover:underline font-medium flex items-center justify-center sm:justify-start gap-1"
              >
                View Verification Certificate
              </button>
            </div>
          </div>
          </div>{/* /white fill */}
        </div>

        {/* APPROVAL TRAIL */}
        {showApprovalTrail && (
          <div className="no-print mb-4 sm:mb-6">
            <ApprovalTrail approvalChain={approvalChain} integrityStatus={integrityStatus} />
          </div>
        )}

        {/* WHY THIS REPORT'S CHAIN IS DIFFERENT.
            Shown to every reviewer, not just the admin who chose it: an auditor or
            global director signing a report whose regional check was reassigned or
            removed is entitled to know that, and why, without having to ask. */}
        {report?.approvalPathReason && isReviewerRole && (
          <div className="rpt-route-note bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-4 sm:mb-6">
            <div className="flex items-start gap-3">
              <span className="text-amber-600 text-lg leading-none mt-0.5">!</span>
              <div className="min-w-0">
                <p className="font-semibold text-amber-900">
                  {report.approvalPath === 'skip_regional'
                    ? 'This report skips regional director review'
                    : `Regional approval reassigned to ${report.regionalApproverName || 'another approver'}`}
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  <span className="font-medium">Route:</span>{' '}
                  Auditor{' '}
                  {report.approvalPath === 'skip_regional'
                    ? ''
                    : `-> ${report.regionalApproverName || 'named approver'} `}
                  -&gt; Global/Finance Director
                </p>
                <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap break-words">
                  <span className="font-medium">Reason given by the admin:</span>{' '}
                  {report.approvalPathReason}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PRIMARY NAVIGATION BAR - the walkthrough only. Deciding the report
            (Submit to Auditor, Approve, Send Back) sits at the foot of the page,
            under the claims and the comments box, so it is done after reading
            the report rather than from above it. */}
        {canMassReview && (
          <div className="no-print bg-white rounded-xl shadow-lg p-4 mb-4 sm:mb-6 flex flex-wrap gap-4 items-center">
            {/* Guided claim-by-claim review: opens the split-screen walkthrough
                (claim on the left, its supporting documents on the right) so the
                reviewer never has to leave the report to check a receipt. */}
            <button
              onClick={() => setShowMassReview(true)}
              title={isMyReviewStage
                ? 'Review each claim beside its supporting documents'
                : `Read-only: this report is at "${getReportStatusLabel(report.status)}", so you cannot mark claims yet`}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 justify-center px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors font-medium flex items-center gap-2"
            >
              <span aria-hidden="true">⇔</span>
              {isAuditor ? 'Mass Audit' : 'Mass Review'}
              {!isMyReviewStage && <span className="text-xs font-normal opacity-80">(view only)</span>}
            </button>

            {/* No Delete here: once submitted, a report is part of the audit
                record and is superseded by generating a new one. A draft is
                deleted from the foot of the page, beside Submit to Auditor. */}
          </div>
        )}

        {/* REVIEW PROGRESS BAR - counts the signed-in reviewer's own per-claim marks
            (the auditor's individual audits, or each director's review marks). */}
        {canMassReview && (() => {
          const total    = report.claims.length;
          const reviewed = report.claims.filter(claimReviewedByCurrentRole).length;
          const pct      = Math.round((reviewed / total) * 100);
          const allDone  = reviewed === total;
          const noun     = isAuditor ? 'individual audit' : 'review';
          return (
            <div className={`no-print rounded-xl shadow-lg p-4 sm:p-5 mb-4 sm:mb-6 ${allDone ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-start sm:items-center justify-between gap-3 mb-2">
                <span className={`text-sm font-semibold ${allDone ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {allDone
                    ? `✓ All claims ${reviewProgressLabel.toLowerCase()}`
                    : `${isAuditor ? 'Individual Audit' : `${directorRoleLabel} Review`} Progress`}
                </span>
                <span className={`text-sm font-bold whitespace-nowrap tabular-nums ${allDone ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {reviewed} / {total} claims
                </span>
              </div>
              <div className="w-full bg-white rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-2.5 rounded-full transition-all duration-500 ${allDone ? 'bg-emerald-500' : 'bg-amber-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {!allDone && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-2">
                  <p className="text-xs text-amber-700">
                    {total - reviewed} claim{total - reviewed !== 1 ? 's' : ''} still pending {noun}.
                    Walk them side by side with their receipts, or approve the full report at once.
                  </p>
                  <button
                    onClick={() => setShowMassReview(true)}
                    className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm sm:text-xs font-semibold bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    {isAuditor ? 'Mass Audit' : 'Mass Review'} →
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* CLAIMS TABLE */}
        <div className="rpt-claims-card bg-white rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-6">
          <div className="rpt-claims-scroll overflow-x-auto">
            {/* Below md each claim becomes a labelled card (index.css:
                RESPONSIVE TABLES + mobile:report-page); md+ is the table. */}
            <table className="w-full responsive-table report-claims-table">
              <thead className="bg-gray-800 text-white">
                <tr>
                  {/* rpt-col-* name each column for the print rules (index.css:
                      MONTHLY REPORT - PRINTED DOCUMENT), which must not go by
                      position - three of these columns depend on the viewer. */}
                  <th className="rpt-col-toggle px-3 py-3 text-left text-sm font-semibold w-8"></th>
                  <th className="rpt-col-name px-3 py-3 text-left text-sm font-semibold">Name</th>
                  <th className="rpt-col-type px-3 py-3 text-left text-sm font-semibold">Type</th>
                  <th className="rpt-col-perdiem px-3 py-3 text-right text-sm font-semibold">Per Diem</th>
                  <th className="rpt-col-reimb px-3 py-3 text-right text-sm font-semibold">Reimbursements</th>
                  <th className="rpt-col-total px-3 py-3 text-right text-sm font-semibold">Total</th>
                  <th className="rpt-col-ptae px-3 py-3 text-left text-sm font-semibold">PTA-E</th>
                  <th className="rpt-col-alloc px-3 py-3 text-left text-sm font-semibold">Allocated Amount</th>
                  {/* Line-item comments are an admin/auditor working channel - not carried up the approval chain. */}
                  {!isDirector && <th className="rpt-col-comments px-3 py-3 text-left text-sm font-semibold">Comments</th>}
                  {isAuditor && <th className="rpt-col-audit px-3 py-3 text-left text-sm font-semibold">Audit</th>}
                  <th className="rpt-col-actions px-3 py-3 text-left text-sm font-semibold">Actions</th>
                  <th className="rpt-col-status px-3 py-3 text-left text-sm font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedReportClaims.map((claim, index) => renderClaimRow(claim, index))}
                {/* Printed total: each figure under its own column (the screen
                    row in the tfoot spans columns that do not print). Last row
                    of the tbody rather than in the tfoot: Chrome keeps a row
                    with the one before it (break-before: avoid) only within
                    one row group, and in a tfoot the total could be printed
                    alone under a repeated header on a page of its own. */}
                <tr className="rpt-screen-hide rpt-total-row">
                  <td className="rpt-col-name" colSpan={2}>Total Amount</td>
                  <td className="rpt-col-perdiem">
                    {formatCurrency((report.claims || []).reduce((t, c) => t + (Number(c.perDiem) || 0), 0))}
                  </td>
                  <td className="rpt-col-reimb">
                    {formatCurrency((report.claims || []).reduce((t, c) => t + (Number(c.reimbursements) || 0), 0))}
                  </td>
                  <td className="rpt-col-total">{formatCurrency(report.totalAmount)}</td>
                  {/* PTA-E, Allocated Amount, Comments (not for directors), Status */}
                  <td colSpan={isDirector ? 3 : 4}></td>
                </tr>
              </tbody>
              <tfoot className="bg-gray-100">
                <tr className="no-print">
                  <td colSpan={isAuditor ? "7" : "5"} className="px-3 py-3 text-right font-semibold text-gray-800">
                    Total Amount:
                  </td>
                  <td colSpan={isAuditor ? "7" : "6"} className="px-3 py-3 text-right font-bold text-gray-800 text-lg tabular-nums">
                    {formatCurrency(report.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* FUNDING BY PTA-E - print only, as the official PDF has it
            (utils/reportPdfContent.mjs): what each code carries across the
            report, from a claim's splits or its one code for the whole claim. */}
        {(() => {
          const byCode = new Map();
          (report.claims || []).forEach((c, i) => {
            const parts = Array.isArray(c.splits) && c.splits.length > 0
              ? c.splits.map(sp => ({ code: sp.ptae || '', amount: Number(sp.amount) || 0 }))
              : [{ code: c.ptae || '', amount: Number(c.total ?? c.amount) || 0 }];
            parts.forEach(p => {
              const entry = byCode.get(p.code) || { amount: 0, claims: new Set() };
              entry.amount += p.amount;
              entry.claims.add(String(c.claimId ?? `row-${i}`));
              byCode.set(p.code, entry);
            });
          });
          if (!byCode.size) return null;
          const rows = [...byCode.entries()]
            .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
          return (
            <div className="rpt-screen-hide rpt-funding">
              <h3>Funding by PTA-E</h3>
              <table>
                <thead>
                  <tr>
                    <th>PTA-E</th>
                    <th className="rpt-num">Claims</th>
                    <th className="rpt-num">Amount (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(([code, entry]) => (
                    <tr key={code || 'not-assigned'}>
                      <td className="rpt-code">{code ? getPTAEDisplay(code) : 'Not assigned'}</td>
                      <td className="rpt-num">{entry.claims.size}</td>
                      <td className="rpt-num">{formatCurrency(entry.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Allocated</td>
                    <td></td>
                    <td className="rpt-num">{formatCurrency(rows.reduce((t, [, entry]) => t + entry.amount, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })()}

        {/* FOOTER SECTION */}
        <div className="rpt-footer bg-white rounded-xl shadow-lg p-4 sm:p-6">
          {/* REPORT METADATA */}
          <div className="rpt-meta grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <span className="text-sm text-gray-500">Prepared By:</span>
              <p className="font-medium text-gray-800">
                {report.preparedByUser ?
                  `${report.preparedByUser.firstName} ${report.preparedByUser.lastName}` :
                  report.preparer ?
                    `${report.preparer.firstName} ${report.preparer.lastName}` :
                    'Unknown'
                }
              </p>
            </div>
            {report.approvedBy && (
              <div>
                <span className="text-sm text-gray-500">Approved By:</span>
                <p className="font-medium text-gray-800">
                  {`${report.approvedBy.firstName} ${report.approvedBy.lastName}`}
                </p>
              </div>
            )}
            <div>
              <span className="text-sm text-gray-500">Created On:</span>
              <p className="font-medium text-gray-800">
                 {new Date(report.createdAt).toLocaleString('en-UK', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
              </p>
            </div>
          {report.status === 'approved' && (
            <div>
              <span className="text-sm text-gray-500">Approved On:</span>
              <p className="font-medium text-gray-800">
                {report.approvedAt ?
                  new Date(report.approvedAt).toLocaleString('en-UK', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  }) : 'Date not available'
                }
              </p>
            </div>
          )}
          </div>

          {/* COMMENTS SECTION */}
          {/* Printed as text; an editable box nobody filled in (placeholder
              showing) does not print at all. */}
          <div className="rpt-comments space-y-4 mb-6">
            {/* Admin can edit their own comments when report is editable */}
            {isAdmin && canEditReport() && (
              <div className={adminComments.trim() ? undefined : 'no-print'}>
                <label htmlFor="admin-comments" className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Comments:
                </label>
                <textarea
                  id="admin-comments"
                  value={adminComments}
                  onChange={(e) => {
                    setAdminComments(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="no-print w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  placeholder="Add admin notes about this report..."
                  rows="3"
                />
                {adminComments.trim() && <p className="rpt-screen-hide rpt-comment-text">{adminComments}</p>}
              </div>
            )}

            {/* Auditor can edit their comments when reviewing */}
            {isAuditor && canAuditorReview() && (
              <div className={auditorComments.trim() ? undefined : 'no-print'}>
                <label htmlFor="auditor-comments" className="block text-sm font-medium text-gray-700 mb-2">
                  Auditor Comments:
                </label>
                <textarea
                  id="auditor-comments"
                  value={auditorComments}
                  onChange={(e) => setAuditorComments(e.target.value)}
                  className="no-print w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  placeholder="Add auditor comments about this report..."
                  rows="4"
                />
                {auditorComments.trim() && <p className="rpt-screen-hide rpt-comment-text">{auditorComments}</p>}
              </div>
            )}

            {/* Display existing admin comments to auditor (read-only) */}
            {report.adminComments && isAuditor && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Comments:</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-700 break-words">
                  {report.adminComments}
                </div>
              </div>
            )}

            {/* Display existing auditor comments to admin (read-only) */}
            {report.auditorComments && isAdmin && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Auditor Comments:</h4>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-gray-700 break-words">
                  {report.auditorComments}
                </div>
              </div>
            )}

            {/* The stand-in's cover has ended: the report is theirs to read, not
                to decide. */}
            {coverEndedAt && (
              <div className="no-print bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-4">
                <p className="font-semibold text-amber-900">
                  Your cover for this report ended on {coverEndText(coverEndedAt)}
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  It&apos;s back with the regional director, so you can no longer approve or send it back.
                </p>
              </div>
            )}

            {/* Final approval was handed to someone else. Said before they start,
                not only when they press Approve. */}
            {finalNamedOther && (
              <div className="no-print bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-4">
                <p className="font-semibold text-amber-900">
                  The admin named {finalNamedOther} to give final approval
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  You can still approve it: you will be asked to confirm taking it over,
                  and {finalNamedOther} will be told by email.
                </p>
              </div>
            )}

            {/* Whoever is deciding this stage edits their comments here -
                gated on the ability to approve, not on holding a director role,
                since a named stand-in has to be able to write the comment that
                approval requires. */}
            {canApproveAsDirector && (
              <div className={directorComments.trim() ? undefined : 'no-print'}>
                <label htmlFor="director-comments" className="block text-sm font-medium text-gray-700 mb-2">
                  {directorRoleLabel} Comments:
                  <span className="no-print text-stanford-red ml-1" title="Required to approve">*</span>
                </label>
                <textarea
                  id="director-comments"
                  value={directorComments}
                  onChange={(e) => {
                    setDirectorComments(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="no-print w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  placeholder="Enter your comments - required before approving..."
                  rows="4"
                />
                {directorComments.trim() && <p className="rpt-screen-hide rpt-comment-text">{directorComments}</p>}
              </div>
            )}

            {/* Display existing regional director comments (read-only for others) */}
            {report.regionalDirectorComments && !isRegionalDirector && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Regional Director Comments:</h4>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-gray-700 break-words">
                  {report.regionalDirectorComments}
                </div>
              </div>
            )}

            {/* Display existing global director comments (read-only for others) */}
            {report.globalDirectorComments && !(isGlobalDirector || isFinanceDirector) && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Global Director Comments:</h4>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-gray-700 break-words">
                  {report.globalDirectorComments}
                </div>
              </div>
            )}
          </div>

          {/* CLARIFICATION QUESTIONS - asked instead of sending the report or a
              claim back, and answered here or from the email link. Not
              printed: the conversations travel in the report's zips instead. */}
          {(isAdmin || canAskQuestions) && (
            <section className="no-print mb-6" aria-labelledby="report-questions-title">
              <h3 id="report-questions-title" className="text-base font-semibold text-gray-900 mb-1">Questions</h3>
              <p className="text-xs text-gray-500 mb-3">
                Questions about this report and its claims. Asking sends nothing back - the report stays where it
                is while the question is answered.
              </p>
              <ClarificationPanel
                reportId={report._id}
                canAsk={canAskQuestions
                  && ['submitted', 'auditor_approved', 'regional_director_approved'].includes(report.status)}
                emptyText="No questions have been asked about this report or its claims."
              />
            </section>
          )}

          {/* ACTION BUTTONS SECTION */}
          <div className="no-print flex flex-col-reverse sm:flex-row flex-wrap gap-4 justify-between pt-4 sm:pt-6 border-t border-gray-200">
            {/* Phones: a 2-up grid of secondary actions under the decision
                buttons; an odd one out spans the row. */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 max-sm:empty:hidden [&>*:last-child:nth-child(odd)]:col-span-2">
              {/* Admin save progress */}
              {canEditReport() && isAdmin && (
                <button
                  onClick={handleSaveProgress}
                  disabled={saving}
                  className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {saving ? ' Saving...' : ' Save Progress'}
                </button>
              )}

              {/* Pull in claims approved after this draft was generated */}
              {isAdmin && report.status === 'draft' && (
                <button
                  onClick={openPullModal}
                  disabled={pullingClaims || saving}
                  className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-stanford-red text-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors disabled:opacity-50"
                  title="Select approved claims to add to this report"
                >
                  ↧ Pull in Approved Claims
                </button>
              )}

              {/* NEW: Auditor save progress */}
              {canAuditorSave() && isAuditor && (
                <button
                  onClick={handleSaveProgress}
                  disabled={saving}
                  className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {saving ? ' Saving...' : ' Save Progress'}
                </button>
              )}

              {/* Save progress - same audience as the comments box above. */}
              {canApproveAsDirector && (
                <button
                  onClick={handleSaveProgress}
                  disabled={saving}
                  className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {saving ? ' Saving...' : ' Save Comments'}
                </button>
              )}

            {/* Show export buttons for approved, pending, or submitted reports */}
              {(report.status === 'auditor_approved' || report.status === 'regional_director_approved' || report.status === 'global_director_approved' || report.status === 'submitted') && (
                <>
                  {/* Download Full PDF button */}
                  <button
                    onClick={handleDownloadFullReportPDF}
                    disabled={downloadingFullPDF}
                    className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    title="Download full report as PDF with claims and certificate"
                  >
                    {downloadingFullPDF ? 'Generating...' : 'Download PDF'}
                  </button>
                  {/* Export to Excel button */}
                  <button
                    onClick={exportToExcel}
                    className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Export report to Excel"
                  >
Export Excel
                  </button>
                  {/* Download Supporting Documents button */}
                  <button
                    onClick={handleDownloadSupportingDocs}
                    disabled={downloadingSupportingDocs}
                    className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    title="Download all supporting documents"
                  >
                    {downloadingSupportingDocs ? 'Downloading...' : 'Supporting Docs'}
                  </button>
                  {/* The final-approval archive - only a finished report has one */}
                  {report.status === 'global_director_approved' && (
                    <button
                      onClick={handleDownloadApprovalArchive}
                      disabled={downloadingArchive}
                      className="min-h-[44px] sm:min-h-0 px-3 sm:px-4 py-2 max-sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                      title="Download the report, every claim and every receipt as one zip"
                    >
                      {downloadingArchive ? 'Building archive...' : 'Approval Archive'}
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 max-sm:empty:hidden">
              {/* Every decision on the report lives here, once, under the claims
                  and comments. Submit to Auditor opens the approval-path modal -
                  never call handleSendToAuditor from a click directly: an older
                  copy did, posted the click event as the regional approver id,
                  and every submit from it failed. */}
              {canSendToAuditor() && (
                <button
                  onClick={() => openSubmitPathModal()}
                  disabled={sendingToAuditor}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-2 max-sm:font-semibold bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
                >
                  {sendingToAuditor ? 'Sending...' : 'Submit to Auditor'}
                </button>
              )}

              {/* Drafts only. Not held back by unsaved changes as Submit is:
                  deleting the draft discards them along with it. */}
              {isAdmin && report.status === 'draft' && (
                <button
                  onClick={handleDeleteDraft}
                  disabled={saving || sendingToAuditor}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-2 max-sm:font-semibold border border-gray-300 text-gray-700 rounded-lg hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Working...' : 'Delete Draft'}
                </button>
              )}

              {canApproveAsDirector && (
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <button
                    onClick={() => setShowRejectReportModal(true)}
                    disabled={actionLoading}
                    className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-2 max-sm:font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Send Back for Revision'}
                  </button>
                  <button
                    onClick={() => handleDirectorApprove()}
                    disabled={actionLoading}
                    className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-2 max-sm:font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : (isGlobalDirector || isFinanceDirector ? 'Give Final Approval' : 'Approve Report')}
                  </button>
                </div>
              )}

              {canAuditorReview() && (
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  {/* Both actions apply to the WHOLE report. Individual claims are
                      signed off with the per-row Audit control. */}
                  <button
                    onClick={() => setShowRejectReportModal(true)}
                    disabled={saving || actionLoading}
                    className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-2 max-sm:font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Send Back for Revision'}
                  </button>
                  <button
                    onClick={() => handleAuditorApproval(true)}
                    disabled={saving || actionLoading}
                    className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-2 max-sm:font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? ' Processing...' : '✓ Approve Report'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SIGN-OFF TRAIL - print only: the approval history belongs on the
            printed record whether or not it is open on screen (the on-screen
            copy above does not print, so it never appears twice). If it has
            not loaded, the paper says so rather than silently ending here. */}
        {approvalChain ? (
          <div className="rpt-screen-hide rpt-print-trail">
            <ApprovalTrail approvalChain={approvalChain} integrityStatus={integrityStatus} />
          </div>
        ) : (
          <div className="rpt-screen-hide rpt-print-trail rpt-trail-missing">
            <strong>Approval history</strong> was not available when this page was printed, so the
            sign-off trail is not included.
            {integrityStatus && (integrityStatus.valid ? ' Integrity Verified.' : ' Integrity Issue detected.')}
          </div>
        )}
      </div>
      </PageShell>

      {/* MODALS - siblings of the shell, like the notification. */}

      {/* Pull in Approved Claims - selection modal */}
      {showPullModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[calc(100dvh-1.5rem)] sm:max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Add Approved Claims</h3>
              <button onClick={() => setShowPullModal(false)} aria-label="Close" className="-mr-2 sm:mr-0 w-11 h-11 sm:w-auto sm:h-auto flex-shrink-0 text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto">
              {loadingAvailable ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin"></div>
                </div>
              ) : availableClaims.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No approved claims are available to add for this period.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-sm text-gray-600">Select the claims to add to this report.</p>
                    <button
                      onClick={() => setSelectedPullIds(prev => prev.size === availableClaims.length ? new Set() : new Set(availableClaims.map(c => c.claimId)))}
                      className="min-h-[44px] sm:min-h-0 flex-shrink-0 whitespace-nowrap text-xs text-stanford-red hover:underline"
                    >
                      {selectedPullIds.size === availableClaims.length ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {[...availableClaims].sort(byIndividual).map((c) => (
                      /* Phones: name and amount on the first line, type and
                         date on the second, so the name is never cut to "E…". */
                      <label key={c.claimId} className="grid grid-cols-[auto_1fr_auto] sm:flex items-center gap-x-3 gap-y-1 sm:gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPullIds.has(c.claimId)}
                          onChange={() => togglePullId(c.claimId)}
                          className="row-span-2 sm:row-span-1 w-5 h-5 sm:w-4 sm:h-4 text-stanford-red border-gray-300 rounded focus:ring-stanford-red"
                        />
                        <span className={`order-3 sm:order-none justify-self-start text-xs px-2 py-0.5 rounded ${c.claimType === 'ExpenseClaim' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                          {c.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem'}
                        </span>
                        <span className="min-w-0 flex-1 text-sm font-medium text-gray-800 break-words sm:truncate">{c.userName || '-'}</span>
                        <span className="text-right text-sm font-semibold text-gray-700 tabular-nums">{formatCurrency(c.amount)}</span>
                        <span className="order-4 sm:order-none text-right text-xs text-gray-400 whitespace-nowrap">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowPullModal(false)} className="min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">Cancel</button>
              <button
                onClick={handleConfirmPull}
                disabled={pullingClaims || selectedPullIds.size === 0}
                className="min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
              >
                {pullingClaims ? 'Adding…' : `Add selected${selectedPullIds.size > 0 ? ` (${selectedPullIds.size})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split Modal */}
      {showSplitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Split Claim into Multiple PTAE Allocations</h3>
              <button
                onClick={() => setShowSplitModal(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                &times;
              </button>
            </div>

            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700">Original Amount: <strong className="text-gray-900">{formatCurrency(report.claims.find(c => String(c.claimId) === String(showSplitModal))?.total || 0)}</strong></p>
                <p className="text-sm text-gray-500 mt-1">Split this amount across different PTAE codes:</p>
              </div>

              <div className="space-y-4">
                {splitData.splits.map((split, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800">Split {index + 1}</h4>
                      {splitData.splits.length > 2 && (
                        <button
                          onClick={() => handleRemoveSplit(index)}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount:</label>
                        <input
                          type="number"
                          step="0.01"
                          value={split.amount || ' '}
                          onChange={(e) => handleSplitChange(index, 'amount', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                          placeholder="Enter amount"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PTAE:</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={split.ptaeSearch || ''}
                            onChange={(e) => {
                              handleSplitChange(index, 'ptaeSearch', e.target.value);
                              handleSplitChange(index, 'showPtaeDropdown', true);
                            }}
                            onFocus={() => handleSplitChange(index, 'showPtaeDropdown', true)}
                            onBlur={(e) => {
                              setTimeout(() => {
                                const container = e.target.closest('.relative');
                                const dropdown = container?.querySelector('.absolute');
                                if (!dropdown || !dropdown.matches(':hover')) {
                                  handleSplitChange(index, 'showPtaeDropdown', false);
                                }
                              }, 150);
                            }}
                            placeholder="Search PTAE by code or description..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                          />

                          {split.showPtaeDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {(() => {
                                const searchTerm = (split.ptaeSearch || '').toLowerCase().trim();

                                // If no search term, show all PTAEs (first 10)
                                if (searchTerm === '') {
                                  const displayedPtaes = ptaes.slice(0, 10);
                                  return displayedPtaes.map((ptae, ptaeIndex) => (
                                    <div
                                      key={`${ptae.ptaeId}-${ptaeIndex}`}
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => handleSplitPTAESelection(index, ptae)}
                                    >
                                      <span className="font-mono text-sm text-stanford-red">{ptae.ptaeId}</span>
                                      <span className="text-sm text-gray-500 ml-2">{ptae.description}</span>
                                    </div>
                                  ));
                                }

                                // Filter PTAEs based on search term
                                const filteredPtaes = ptaes.filter(ptae => {
                                  if (!ptae) return false;

                                  const ptaeId = ptae.ptaeId ? ptae.ptaeId.toString().toLowerCase() : '';
                                  const description = ptae.description ? ptae.description.toString().toLowerCase() : '';

                                  return ptaeId.includes(searchTerm) ||
                                         description.includes(searchTerm);
                                });

                                // Show first 10 results
                                const displayedPtaes = filteredPtaes.slice(0, 10);

                                if (displayedPtaes.length === 0) {
                                  return (
                                    <div key="no-results" className="px-4 py-3 text-center text-gray-500">
                                      No PTAE found matching "{split.ptaeSearch}"
                                    </div>
                                  );
                                }

                                return displayedPtaes.map((ptae, ptaeIndex) => (
                                  <div
                                    key={`${ptae.ptaeId}-${ptaeIndex}`}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSplitPTAESelection(index, ptae)}
                                  >
                                    <span className="font-mono text-sm text-stanford-red">{ptae.ptaeId}</span>
                                    <span className="text-sm text-gray-500 ml-2">{ptae.description}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                          {split.ptae && (
                            <div className="mt-2 text-sm text-gray-600">
                              Selected: <strong className="text-stanford-red">{ptaes.find(p => p.ptaeId === split.ptae)?.ptaeId}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mt-6">
                <p className="text-gray-700">
                  Total Split: <strong className="text-gray-900">{formatCurrency(getTotalSplitAmount())}</strong>
                </p>
                <p className={`text-sm mt-1 ${getRemainingAmount() < 0 ? 'text-red-600' : getRemainingAmount() > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  Remaining: <strong>{formatCurrency(getRemainingAmount())}</strong>
                </p>
              </div>

              <button
                onClick={handleAddSplit}
                className="mt-4 w-full px-4 py-2 border border-dashed border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                + Add Another Split
              </button>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowSplitModal(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmSplit(showSplitModal)}
                className="px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
                disabled={Math.abs(getRemainingAmount()) > 0.01 || splitData.splits.some(s => !s.ptae)}
              >
                Confirm Split
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guided claim-by-claim review: 50/50 split of the claim and its supports */}
      {showMassReview && (
        <MassReviewOverlay
          title={isAuditor ? 'Mass Audit' : 'Mass Review'}
          subtitle={`${report.month}/${report.year} · ${report.region}`}
          claims={sortedReportClaims}
          role={user?.role}
          loadBundle={(claim) =>
            monthlyReportService.getClaimReviewBundle(report._id, claim.claimId, claim.claimType)}
          initiallyDone={claimReviewedByCurrentRole}
          onClose={() => setShowMassReview(false)}
          onMark={(claimId, claimType, options) => markClaimReviewed(claimId, claimType, user?.role, '', options)}
          onFlag={flagClaimInReport}
          // Report-level actions only when it is actually this reviewer's turn.
          // Opening the walkthrough on someone else's stage is read-only.
          onApprove={isMyReviewStage ? handleMassReviewApprove : null}
          onSendBack={isMyReviewStage ? handleMassReviewSendBack : null}
          approving={actionLoading || saving}
          reportId={report._id}
        />
      )}

      {/* One claim's clarification questions, opened from its row badge. */}
      {questionClaim && (
        <ClarificationDrawer
          title="Questions about this claim"
          subtitle={`${questionClaim.userName || 'Claim'} · ${questionClaim.claimType === 'PerDiemClaim' ? 'Per Diem' : 'Expense'}`}
          claimType={questionClaim.claimType}
          claimId={questionClaim.claimId}
          canAsk={canAskQuestions}
          onClose={() => setQuestionClaim(null)}
        />
      )}

      {/* Who approves at the regional-director stage. Naming a person rather
          than offering "skip" is deliberate: when a director was away the only
          option used to be deleting the stage, which lost the review instead of
          covering it. Removing the stage is still possible, but it is now the
          explicit last choice rather than the only alternative. */}
      {showSubmitPathModal && (
        <div className="fixed inset-0 z-[9300] bg-black/50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-[85vh]">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Submit to auditor</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                This is the route your report will take.
              </p>
            </div>

            <div className="p-4 sm:p-6 space-y-3 overflow-y-auto">
              {!approvalRoute ? (
                <p className={`text-sm ${routeError ? 'text-red-700' : 'text-gray-500'}`}>
                  {routeError || 'Working out the approval route…'}
                </p>
              ) : (
                <>
                  {/* Read-only, in the order they sign. Covering for an absent
                      director is the director's delegation, not the admin's
                      choice (2026-10-22) - it is shown here so the admin knows
                      who the report will reach. */}
                  <ol className="space-y-3">
                    <li className="border border-gray-200 rounded-lg p-3">
                      <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">1. Auditor</span>
                      <span className="block text-sm text-gray-800 mt-1">The auditor reviews first.</span>
                    </li>

                    <li className={`border rounded-lg p-3 ${regionalNeedsChoice ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                      <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">2. Regional Director</span>
                      {regionalNeedsChoice ? (
                        <>
                          <p className="text-sm text-amber-900 mt-1">
                            No regional director is available for {report?.region || 'this region'} - none is on the
                            system, or their account is suspended - and nobody is covering. Choose who approves this
                            stage; a global director chosen here may give final approval as well.
                          </p>
                          {approvalRoute.regional.candidates.length > 0 && (
                            <select
                              aria-label="Who approves the regional stage"
                              value={regionalPick}
                              onChange={(e) => setRegionalPick(e.target.value)}
                              className="mt-2 w-full min-h-[44px] sm:min-h-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red"
                            >
                              <option value="">Choose…</option>
                              {approvalRoute.regional.candidates.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name} - {String(c.role || '').replace(/_/g, ' ')}
                                </option>
                              ))}
                            </select>
                          )}
                        </>
                      ) : (
                        <>
                          {approvalRoute.regional.directors.map(d => (
                            <span key={d.id} className="block text-sm text-gray-800 mt-1">
                              {d.name}{d.email ? <span className="text-gray-500"> &middot; {d.email}</span> : null}
                            </span>
                          ))}
                          {approvalRoute.regional.cover.map(c => (
                            <span key={c.id} className="block text-sm text-sky-800 mt-1">
                              {c.name}, covering{c.coveringFor ? ` for ${c.coveringFor}` : ''}
                              {c.until ? ` until ${formatInstant(c.until)}` : ''}
                            </span>
                          ))}
                          {(approvalRoute.regional.directors.length + approvalRoute.regional.cover.length) > 1 && (
                            <span className="block text-[11px] text-gray-500 mt-1">Any of them can approve.</span>
                          )}
                        </>
                      )}
                    </li>

                    <li className={`border rounded-lg p-3 ${finalNeedsChoice ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                      <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">3. Final Approval</span>
                      {finalNeedsChoice ? (
                        <>
                          <p className="text-sm text-amber-900 mt-1">
                            No global director is available - none is on the system, or their account is
                            suspended - and nobody is covering. Choose who gives final approval.
                          </p>
                          {approvalRoute.final.candidates.length > 0 && (
                            <select
                              aria-label="Who gives final approval"
                              value={finalPick}
                              onChange={(e) => setFinalPick(e.target.value)}
                              className="mt-2 w-full min-h-[44px] sm:min-h-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red"
                            >
                              <option value="">Choose…</option>
                              {approvalRoute.final.candidates.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name} - {String(c.role || '').replace(/_/g, ' ')}
                                </option>
                              ))}
                            </select>
                          )}
                        </>
                      ) : (
                        <>
                          {approvalRoute.final.directors.map(d => (
                            <span key={d.id} className="block text-sm text-gray-800 mt-1">
                              {d.name} <span className="text-gray-500">&middot; global director</span>
                            </span>
                          ))}
                          {approvalRoute.final.cover.map(c => (
                            <span key={c.id} className="block text-sm text-sky-800 mt-1">
                              {c.name}, covering{c.coveringFor ? ` for ${c.coveringFor}` : ''}
                              {c.until ? ` until ${formatInstant(c.until)}` : ''}
                            </span>
                          ))}
                          {approvalRoute.final.financeDirectors.length > 0 && (
                            <span className="block text-[11px] text-gray-500 mt-1">
                              The finance director ({approvalRoute.final.financeDirectors.map(d => d.name).join(', ')}) can also give final approval.
                            </span>
                          )}
                        </>
                      )}
                    </li>
                  </ol>

                  <p className="text-xs text-gray-500">
                    Directors set their own cover while they are away. Whoever is covering when the report
                    reaches them can approve it, and is emailed.
                  </p>
                </>
              )}
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <p className="text-xs text-gray-500 min-w-0 flex-1 empty:hidden sm:empty:block">{submitBlockedBecause}</p>
              <div className="flex flex-col-reverse sm:flex-row gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowSubmitPathModal(false)}
                  disabled={sendingToAuditor}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const path = pathPayload();
                    setShowSubmitPathModal(false);
                    const signature = await requestSignature({
                      reportId,
                      title: 'Sign to submit this report',
                      reason: 'Prepared and submitted this report',
                      applyLabel: 'Sign and submit',
                    });
                    // Backing out of signing returns to the route, with any
                    // choice still made.
                    if (!signature) { setShowSubmitPathModal(true); return; }
                    handleSendToAuditor({ ...path, signature });
                  }}
                  disabled={sendingToAuditor || !!submitBlockedBecause}
                  title={submitBlockedBecause}
                  className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-50 font-medium"
                >
                  {sendingToAuditor ? 'Submitting…' : 'Submit to Auditor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Whole-report send-back modal (auditor and directors) */}
      <RejectReportModal
        isOpen={showRejectReportModal}
        onClose={() => setShowRejectReportModal(false)}
        onReject={handleReportSendBack}
        loading={actionLoading}
      />

      {/* Verification Certificate Modal */}
      {showVerificationCertificate && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto"
          onClick={() => setShowVerificationCertificate(false)}
        >
          <div
            className="bg-gray-100 rounded-xl max-w-5xl w-full my-0 sm:my-8 max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center z-10">
              <h2 className="text-lg font-semibold text-gray-800">Verification Certificate</h2>
              <button
                onClick={() => setShowVerificationCertificate(false)}
                aria-label="Close"
                className="-mr-2 sm:mr-0 w-11 h-11 sm:w-auto sm:h-auto flex-shrink-0 text-gray-400 hover:text-gray-600 text-2xl font-light"
              >
                &times;
              </button>
            </div>
            <div className="p-2 sm:p-4">
              {loadingCertificate ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stanford-red"></div>
                </div>
              ) : verificationCertificate ? (
                <VerificationCertificate
                  certificate={verificationCertificate}
                  onDownloadPDF={handleDownloadVerificationPDF}
                  isDownloading={downloadingPDF}
                />
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Failed to load verification certificate
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archived versions of this report, opened from the Version tile. */}
      {showVersions && (
        <ReportVersionViewer
          reportId={report?.id || reportId}
          onClose={() => setShowVersions(false)}
        />
      )}
    </>
  );
};

export default MonthlyReportPage;
